import { CommonDebugger } from '../../../entities';
import type { VAxios } from '../../define/axios/Axios';
import { PageWrapper } from '../../entities/base/base.entities';
import { IBatchFile, IBatchTag, IBatchTagGroup } from '../../entities/cc/batch.entities';
import { chunk } from 'lodash-es';
import { AxiosRequestConfig } from 'axios';
import type { RequestOptions } from '../../types/axios';

export interface AddCutDrawingCanvasParams {
  admin?: boolean;
  batchId?: string | number;
  canvasIds?: string[];
  cutMeta?: Record<string, unknown>;
}

export interface AddCutDrawingCanvasRes {
  code: string;
  message: string;
}

export interface CapacityDetailInfo {
  capacity?: number;
  duration?: number;
  expireTime?: string;
  orderTitle?: string;
  sourceLabel?: string;
  startTime?: string;
  status?: 'normal' | 'refund' | 'expired' | string;
  tradeNo?: string;
}

export type CreatorAccountCategory =
  | 'ORDINARY'
  | 'SHOP_OFFICIAL'
  | 'MARKETING'
  | 'AFFILIATE_CREATOR';
export type CreatorType = 'ORDINARY' | 'AFFILIATE';
export type CreatorAccountStatus = 'enabled' | 'disabled';
export type CreatorPublishType = 'TRAFFIC' | 'SHOPPABLE';
export type CreatorPhotoPublishType = 'TRAFFIC' | 'SHOPPABLE' | 'PHOTO_SHOPPABLE' | 'PHOTO_TRAFFIC';
export type CreatorPhotoLinkOption = 'SINGLE_PRODUCT' | 'MULTI_PRODUCT' | 'SHOP';
export type CreatorPublishMode = 'DIRECT' | 'DRAFT';
export type CreatorPrivacyLevel =
  | 'PUBLIC_TO_EVERYONE'
  | 'MUTUAL_FOLLOW_FRIENDS'
  | 'FOLLOWER_OF_CREATOR'
  | 'SELF_ONLY';
export type CreatorPhotoShoppablePermission =
  | 'PHOTO_SHOPPABLE_PERMISSION_PRODUCT'
  | 'PHOTO_SHOPPABLE_PERMISSION_SHOP';
export type CreatorPrecheckStatus = 'NONE' | 'PROCESSING' | 'PASS' | 'REJECT';
export type CreatorPublishStatus =
  | 'waiting'
  | 'allocated'
  | 'executing'
  | 'succeed'
  | 'failed'
  | 'canceled';

export interface CreatorAccountInfo {
  authExpiredTime: Record<string, unknown>;
  avatarUrl: string;
  category: string;
  createBy: string;
  createTime: Record<string, unknown>;
  creatorType: string;
  creatorUsername: string;
  displayName: string;
  id: number;
  isAuthorize: boolean;
  openId: string;
  orgId: number;
  /** 图文带货链接选项权限，用于控制单件/多件商品、店铺页面是否可选 */
  permissions?: CreatorPhotoShoppablePermission[];
  privacyLevels: CreatorPrivacyLevel[];
  region: string;
  shopName: string;
  status: string;
}

export interface AccountsParams {
  category?: CreatorAccountCategory;
  creatorType?: CreatorType;
  isAuthorized?: boolean;
  name?: string;
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  status?: CreatorAccountStatus;
}

/** 创作者账号列表响应，VAxios 已自动解包 result。 */
export interface AccountsRes {
  items: CreatorAccountInfo[];
  pages: number;
  total: number;
}

/** 单个创作者响应，VAxios 已自动解包 result。 */
export type AccountRes = CreatorAccountInfo;

export type DisableRes = void;
export type EnableRes = void;
export type UnbindRes = void;

export interface AuthorizeUrlParams {
  creatorType?: CreatorType;
  /** 账号区域：US-美国，OTHER-欧洲及其他国家/地区（仅带货内容） */
  region?: 'US' | 'OTHER';
}

export type AuthorizeUrlRes = string;

export interface CallbackParams {
  code?: string;
  state?: string;
}

export type CallbackRes = CreatorAccountInfo;

export interface CreatorPublishListItem {
  aiGenerated: number;
  category: string;
  createBy: string;
  createName: string;
  createTime: Record<string, unknown>;
  creatorId: number;
  creatorName: string;
  disableComment: number;
  disableDuet: number;
  disableStitch: number;
  errorMsg: string;
  freeBusiness: number;
  id: number;
  isSchedule: number;
  paidPartnership: number;
  precheckResult: string;
  precheckStatus: string;
  price: string;
  productCoverUrl: string;
  productId: string;
  productTitle: string;
  publishMode: string;
  publishTime: Record<string, unknown>;
  publishType: string;
  status: string;
  videoContentId: string;
  videoCoverUrl: string;
  videoTitle: string;
}

export interface PublishListParams {
  createBy?: number | string;
  creatorIds?: number[];
  endTime?: string;
  page?: number;
  pageSize?: number;
  precheckStatus?: CreatorPrecheckStatus;
  productTitle?: string;
  publishType?: CreatorPublishType;
  sortField?: string;
  sortOrder?: string;
  startTime?: string;
  status?: CreatorPublishStatus;
}

/** 发布记录列表响应，VAxios 已自动解包 result。 */
export interface PublishListRes {
  items: CreatorPublishListItem[];
  pages: number;
  total: number;
}

export interface PhotoPublishLinkProduct {
  linkTitle?: string;
  price?: string;
  productCoverUrl?: string;
  productId?: string;
  productTitle?: string;
}

export interface PhotoPublishPhotoItem {
  contentId?: string;
  imageUrl?: string;
  photoUri?: string;
}

export interface PhotoPublishInfoDTO {
  aiGenerated?: number;
  autoAddMusic?: number;
  brandContentToggle?: number;
  brandOrganicToggle?: number;
  category?: CreatorAccountCategory | string;
  createBy?: string;
  createName?: string;
  createTime?: string;
  creatorId?: number;
  creatorName?: string;
  description?: string;
  disableComment?: number;
  errorCode?: string;
  errorMsg?: string;
  id?: number;
  isSchedule?: number;
  linkOption?: CreatorPhotoLinkOption | string;
  linkProducts?: PhotoPublishLinkProduct[];
  linkTitle?: string;
  musicId?: string;
  photos?: PhotoPublishPhotoItem[];
  privacyLevel?: string;
  productBindMode?: string;
  publishMode?: string;
  publishTime?: string;
  publishType?: CreatorPhotoPublishType | string;
  quota?: string;
  shopGroupId?: string;
  shopGroupName?: string;
  shopGroupType?: number;
  status?: CreatorPublishStatus | string;
  tiktokPublishId?: string;
  title?: string;
}

export interface PhotoPublishListParams {
  createBy?: number | string;
  creatorIds?: number[];
  endTime?: string;
  linkOption?: CreatorPhotoLinkOption | string;
  page?: number;
  pageSize?: number;
  productTitle?: string;
  publishType?: CreatorPhotoPublishType | string;
  sortField?: string;
  sortOrder?: string;
  startTime?: string;
  status?: CreatorPublishStatus | string;
}

/** 图文发布记录列表响应，VAxios 已自动解包 result。 */
export interface PhotoPublishListRes {
  items: PhotoPublishInfoDTO[];
  pages: number;
  total: number;
}

export type CreatorPhotoProductBindMode = 'ONE_LIST' | 'PER_IMAGE';

/** 提交图文发布请求体 */
export interface CreatorPhotoPublishRequest {
  aiGenerated?: number;
  autoAddMusic?: number;
  brandContentToggle?: number;
  brandOrganicToggle?: number;
  creatorId: number;
  description?: string;
  disableComment?: number;
  isSchedule?: number;
  linkOption?: CreatorPhotoLinkOption | string;
  linkProducts?: (PhotoPublishLinkProduct | null)[];
  musicId?: string;
  photos: PhotoPublishPhotoItem[];
  privacyLevel?: string;
  productBindMode?: CreatorPhotoProductBindMode | string;
  publishMode?: CreatorPublishMode | string;
  publishTime?: string;
  publishType: CreatorPhotoPublishType | string;
  shopGroupId?: string;
  shopGroupName?: string;
  shopGroupType?: number;
  title: string;
}

export type CreatorPhotoPublishRes = number;

export interface CategoryCollectionParams {
  creatorId: number;
  nextPage?: string;
  pageSize?: number;
  /** 查询类型:2类目/3合集 */
  searchType: 2 | 3;
}

export interface CategoryCollectionGroup {
  groupId: string;
  groupName: string;
}

/** 店铺类目-合集查询响应，VAxios 已自动解包 result。 */
export interface CategoryCollectionRes {
  groups: CategoryCollectionGroup[];
  nextPage: string;
}

export interface PublishParams {
  aiGenerated?: number;
  bgmId?: string;
  creatorId?: number;
  disableComment?: number;
  disableDuet?: number;
  disableStitch?: number;
  freeBusiness?: number;
  isSchedule?: number;
  paidPartnership?: number;
  precheckEnabled?: number;
  precheckFailContinue?: number;
  privacyLevel?: CreatorPrivacyLevel;
  price?: string;
  productCoverUrl?: string;
  productId?: string;
  productTitle?: string;
  publishMode?: CreatorPublishMode;
  publishTime?: Record<string, unknown>;
  publishType?: CreatorPublishType;
  videoContentId?: string;
  videoCoverUrl?: string;
  videoFileUrl?: string;
  videoSourceRef?: string;
  videoTitle?: string;
}

export type PublishRes = number;

export interface MusicParams {
  creatorId: number;
  keyword?: string;
  pageSize: number;
  pageToken?: string;
  searchId?: string;
}

export interface MusicRes {
  hasMore: boolean;
  musicOptions: {
    author: string;
    coverUrl: string;
    id: string;
    playUrl: string;
    title: string;
  }[];
  nextPageToken: string;
  searchId: string;
}

export type CreatorProductSource = 'SHOP' | 'SHOWCASE';

export interface PrecheckNumRes {
  creatorId: number;
  leftNum: number;
}

export interface ProductsParams {
  creatorId: number;
  keyword?: string;
  pageSize: number;
  pageToken?: string;
  productSource: CreatorProductSource;
}

export interface ProductsRes {
  nextPageToken: string;
  productOptions: {
    currency: string;
    id: string;
    imageUrl: string;
    maxPrice: string;
    minPrice: string;
    price: string;
    title: string;
  }[];
  totalCount: number;
}

export interface PublishDetailRes {
  aiGenerated: number;
  category: string;
  createBy: string;
  createTime: Record<string, unknown>;
  creatorId: number;
  creatorName: string;
  disableComment: number;
  disableDuet: number;
  disableStitch: number;
  errorMsg: string;
  freeBusiness: number;
  id: number;
  isSchedule: number;
  paidPartnership: number;
  precheckResult: string;
  precheckStatus: string;
  price: string;
  productCoverUrl: string;
  productId: string;
  productTitle: string;
  publishMode: string;
  publishTime: Record<string, unknown>;
  publishType: string;
  status: string;
  videoContentId: string;
  videoCoverUrl: string;
  videoTitle: string;
}

export type CancelPublishRes = boolean;
export type DeletePublishRes = boolean;

/** 商品发布信息 */
export interface PublishGoodsInfo {
  batchNo: string;
  createBy: string;
  createTime: Record<string, unknown>;
  errorMsg: string;
  id: number;
  modifyBy: string;
  modifyTime: Record<string, unknown>;
  platformProductId: string;
  shopId: number;
  shopName: string;
  solutionTitle: string;
  solutionUrl: string;
  status: string;
  type: string;
}

/** 查询商品发布信息，VAxios 已自动解包 result。 */
export type QueryPublishGoodsRes = PublishGoodsInfo[];

/** 插件刊登-认领明细结果，VAxios 已自动解包 result。 */
export interface ClaimResult {
  description: string[];
  detailId: number | string;
  detailStatus: string;
  goodsId: number | string;
  images: string[];
  intercepted: boolean;
  message: string | null;
  parentSku: string;
  publishType: number;
  referenceUrl: string;
  title: string;
  variants: {
    extraImages: string[];
    mainImage: string;
    // 变体最前面的列，title表示表头，value表示对应值，比如颜色：red、尺寸：M
    name: { title: string; value: string }[];
    sku: string;
    swatchImage: string;
  }[];
  productIdConfig?: {
    productIdType?: string | null;
    numberPoolId?: string | null;
    numberPoolOptions?: { label: string; value: string }[];
  } | null;
}

/** 插件刊登-认领明细响应 */
export interface ClaimRes {
  code: string;
  message: string;
  result: ClaimResult;
}

/** 插件刊登-回传明细结果参数 */
export interface ReportParams {
  detailId?: number | string;
  message?: string;
  status?: string;
}

/** 插件刊登-回传明细结果响应 */
export interface ReportRes {
  code: string;
  message: string;
  result: boolean;
}

/** 查询店铺详情响应，VAxios 已自动解包 result。 */
export interface GetShopRes {
  authExpiredTime: Record<string, unknown>;
  authorizeId: number;
  createBy: string;
  createTime: Record<string, unknown>;
  groupId: number;
  id: number;
  modifyBy: string;
  modifyTime: Record<string, unknown>;
  name: string;
  operators: {
    name: string;
    userId: number;
  }[];
  region: string;
  sellerType: string;
  spu: Record<string, unknown>[];
  status: string;
  type: string;
}

/** 查询店铺列表参数（POST /aiad/shops/query） */
export interface QueryShopsParams {
  check?: boolean;
  groupId?: string;
  ids?: (string | number)[];
  name?: string;
  page?: number;
  pageSize?: number;
  /** 店铺类型(支持 temu 和 shein),可用值: self,semi,fully */
  shopType?: string;
  sortField?: string;
  sortOrder?: string;
  /** 状态,可用值: enabled,disabled */
  status?: string;
  /** 平台类型 */
  type?: string;
  /** 平台类型列表 */
  types?: string[];
}

/** 查询店铺列表响应，VAxios 已自动解包 result。 */
export interface QueryShopsRes {
  items: GetShopRes[];
  pages: number;
  total: number;
}

/** Temu - 查询分类属性参数 */
export interface TemuAttributesParams {
  catId: number;
  shopId: string;
  /** 是否包含销售属性（规格类型/规格值字典），空白模板规格区需要 true */
  includeSale?: boolean;
}

/** Temu - 类目项 */
export interface TemuCategoryItem {
  catId: number;
  catName: string;
  /** 0 非服饰 / 1 服饰（含鞋履） */
  catType: number;
  /** true=叶子可选类目，无下级；用于 Cascader 是否展示展开箭头 */
  isLeaf?: boolean;
  /** 全量平铺时用于前端组树；缺省表示根或扁平列表 */
  parentCatId?: number;
  children?: TemuCategoryItem[];
  /** 部分类目净重必填（后端有则透传） */
  netWeightRequired?: boolean;
  isNetWeightRequired?: boolean;
  requireNetWeight?: boolean;
  isNetContentRequired?: boolean;
  netContentRequired?: boolean;
}

/** Temu - 搜索类目路径中的单级节点 */
export interface TemuSearchCategoryNode {
  catId: number;
  catLevel: number;
  catName: string;
  catType: number;
  hiddenType?: number;
  isHidden?: boolean;
  isLeaf: boolean;
  parentCatId: number;
}

/** Temu - 搜索类目返回的一条完整路径（cat1…cat9，空级为 null） */
export interface TemuSearchCategoryPath {
  cat1DTO?: TemuSearchCategoryNode | null;
  cat2DTO?: TemuSearchCategoryNode | null;
  cat3DTO?: TemuSearchCategoryNode | null;
  cat4DTO?: TemuSearchCategoryNode | null;
  cat5DTO?: TemuSearchCategoryNode | null;
  cat6DTO?: TemuSearchCategoryNode | null;
  cat7DTO?: TemuSearchCategoryNode | null;
  cat8DTO?: TemuSearchCategoryNode | null;
  cat9DTO?: TemuSearchCategoryNode | null;
}

/** Temu - 尺码组 */
export interface TemuSizechartClassRes {
  catId: number;
  classId: number;
  classType: number;
  parentClassId: number;
  relatedClassIds: (number | string)[];
}

/** Temu - 尺码表元数据 */
export interface TemuSizechartMetaRes {
  allowRange?: boolean;
  sizeSpecMeta: {
    elementList?: { id: number; name: string; necessary: boolean }[];
    elements?: { id: number; name: string; necessary: boolean }[];
    groupList?: { id: number; name: string; unnecessary?: boolean }[];
    groups?: { id: number; name: string; unnecessary?: boolean }[];
    sizeTypeElementsList?: {
      elements: { id: number; name: string; necessary: boolean }[];
      sizeType: number;
    }[];
  };
}

/** Temu - 查询父规格列表参数 */
export interface QueryParentSpecListParams {
  /** 店铺 ID */
  shopId: string;
}

/** Temu - 父规格项 */
export interface TemuParentSpecItem {
  parentSpecId: number;
  parentSpecName: string;
}

/** Temu - 查询父规格列表响应（VAxios 已解包 result） */
export interface QueryParentSpecListRes {
  parentSpecDTOS: TemuParentSpecItem[];
}

/** Temu - 创建自定义规格参数 */
export interface CreateGoodsSpecParams {
  /** 父规格 ID */
  parentSpecId?: number;
  /** 店铺 ID */
  shopId?: string;
  /** 规格值名称 */
  specName?: string;
}

/** Temu - 创建自定义规格响应（VAxios 已解包 result，为规格 ID） */
export type CreateGoodsSpecRes = number;

/** Shein - 创建视频转换任务参数 */
export interface CreateVideoTransformTaskParams {
  /** 视频 URL */
  fileUrl?: string;
  /** 店铺 ID */
  shopId?: string;
}

/** Shein - 视频转换任务结果项 */
export interface VideoTransformTaskItem {
  failReason: string;
  taskId: string;
  /** 任务状态 */
  taskStatus: number;
  transformedUrl: string;
}

/** Shein - 创建视频转换任务响应（VAxios 已解包 result） */
export type CreateVideoTransformTaskRes = VideoTransformTaskItem;

/** Shein - 查询视频转换任务参数 */
export interface GetVideoTransformTasksParams {
  /** 店铺 ID */
  shopId: string;
  /** 转换任务 ID 列表 */
  taskIds: (string | number)[];
}

/** Shein - 查询视频转换任务响应（VAxios 已解包 result） */
export type GetVideoTransformTasksRes = VideoTransformTaskItem[];

/** TikTok - 跨境店铺仓库地址 */
export interface TiktokSellerWarehouseAddress {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  city: string;
  contactPerson: string;
  district: string;
  firstName: string;
  firstNameLocalScript: string;
  fullAddress: string;
  geolocation: {
    latitude: string;
    longitude: string;
  };
  lastName: string;
  lastNameLocalScript: string;
  phoneNumber: string;
  postalCode: string;
  region: string;
  regionCode: string;
  state: string;
  town: string;
}

/** TikTok - 跨境店铺仓库项 */
export interface TiktokSellerWarehouseItem {
  address: TiktokSellerWarehouseAddress;
  effectStatus: string;
  entityId: string;
  id: string;
  isDefault: boolean;
  name: string;
  subType: string;
  type: string;
}

/** TikTok - 查询跨境店铺仓库列表响应（VAxios 已解包 result） */
export interface GetSellerWarehousesRes {
  warehouses: TiktokSellerWarehouseItem[];
}

/** Temu - 查询分类属性响应，VAxios 已自动解包 result。 */
export type TemuAttributesRes = {
  controlType: string;
  inputMaxNum: number;
  isSale: boolean;
  mainSale: boolean;
  name: string;
  numberInputTitle: string;
  parentSpecId: number;
  parentTemplatePid: number;
  pid: number;
  refPid: number;
  referenceType: number;
  required: boolean;
  templatePid: number;
  valueUnit: Record<string, unknown>[];
  templatePropertyValueParentList?: {
    parentVidList: number[];
    vidList: number[];
  }[];
  /** templatePropertyValueParentList 为空时的显隐级联：父 refPid + 父选项 vids */
  showCondition?: {
    parentRefPid: number;
    parentVids: number[];
  }[];
  values: {
    extendInfo: string;
    group: {
      id: number;
      name: string;
    };
    parentVidList: number[];
    specId: number;
    value: string;
    vid: number;
  }[];
}[];

/** Temu 本地 - 查询分类属性参数 */
export interface TemuLocalAttributesParams {
  catId?: number;
  goodsId?: number;
  pid?: number;
  refPid?: number;
  searchText?: string;
  shopId?: string;
  value?: string;
  vid?: number;
}

/** Temu 本地 - 查询分类属性响应，VAxios 已自动解包 result。 */
export type TemuLocalAttributesRes = {
  attributeName: string;
  attributeRules: {
    attributeValueRules: Record<string, unknown>;
    chooseMaxNum: number;
    controlType: number;
    transnationalAttribute: boolean;
  };
  attributeValueDetail: {
    attributeValueList: {
      childAttribute: {
        childAttributeId: number;
        childAttributeValueGroupId: string;
      }[];
      value: string;
      vid: number;
    }[];
    groupId: string;
  }[];
  attributeValueUnitList: {
    valueUnitId: number;
    valueUnitName: string;
  }[];
  /** 属性级父属性 templatePid（联动挂载） */
  parentTemplatePid?: number;
  refPid: number;
  required: boolean;
  templatePid?: number;
  /** 父值 → 子可选值 联动规则 */
  templatePropertyValueParentList?: {
    parentVidList: number[];
    vidList: number[];
  }[];
}[];

/** Temu 本地 - 查询合规补充信息参数 */
export interface TemuLocalComplianceExtraTemplatesParams {
  catId?: number;
  goodsId?: number;
  governPropertyList?: {
    pid?: number;
    refPid?: number;
    value?: string;
    vid?: number;
  }[];
  normalPropertyList?: {
    pid?: number;
    refPid?: number;
    value?: string;
    vid?: number;
  }[];
  searchText?: string;
  shopId?: string;
}

/** Temu 本地 - 查询合规补充信息响应，VAxios 已自动解包 result。 */
export interface TemuLocalComplianceExtraTemplatesRes {
  extraComplianceInfoList: {
    isRequired: boolean;
    type: number;
  }[];
  extraTemplateList: {
    checkTypeName: string;
    isRequired: boolean;
    supportMultiGroup: boolean;
    templateDesc: string;
    templateDimensionType: number;
    templateGoodsPropertyConfigDTOList: {
      refPid: number;
      showSkuVidList: Record<string, unknown>[];
    }[];
    templateId: number;
    templatePropertyDTOList: {
      controlType: number;
      excludeVidMap: Record<string, unknown>;
      inputNum: number;
      inputTitle: string;
      inputType: number;
      max: string;
      min: string;
      parentPid: number;
      parentVidList: Record<string, unknown>[];
      propertyDesc: string;
      propertyName: string;
      propertyValueList: {
        parentVidList: Record<string, unknown>[];
        valueName: string;
        vid: number;
      }[];
      refPid: number;
      selectNum: number;
      unit: string;
      valuePrecision: number;
    }[];
  }[];
  guideFileRequirement: {
    isRequired: boolean;
    requiredLanguageList: Record<string, unknown>[];
  };
}

/** Temu 本地 - 查询运费模板参数 */
export interface TemuLocalFreightTemplatesParams {
  catId?: number;
  goodsId?: number;
  pid?: number;
  refPid?: number;
  searchText?: string;
  shopId?: string;
  value?: string;
  vid?: number;
}

/** Temu 本地 - 查询运费模板响应，VAxios 已自动解包 result。 */
export interface TemuLocalFreightTemplatesRes {
  templateList: {
    templateId: string;
    templateName: string;
  }[];
}

/** Temu 本地 - 查询商品列表参数 */
export interface TemuLocalProductsParams {
  goodsId?: number;
  page?: number;
  pageSize?: number;
  searchText?: string;
  shopId?: string;
  sortField?: string;
  sortOrder?: string;
}

/** Temu 本地 - 查询商品列表响应，VAxios 已自动解包 result。 */
export interface TemuLocalProductsRes {
  items: {
    brandId: number;
    catId: number;
    costTemplateId: string;
    currency: string;
    goodsId: number;
    goodsName: string;
    listPrice: {
      amount: string;
      currency: string;
    };
    outGoodsSn: string;
    price: string;
    quantity: number;
    retailPrice: {
      amount: string;
      currency: string;
    };
    shipmentLimitSecond: number;
    skuInfoList: {
      skuId: number;
      skuSn: string;
      specList: {
        parentSpecId: number;
        parentSpecName: string;
        specId: number;
        specName: string;
      }[];
      stock: number;
    }[];
    thumbUrl: string;
    trademarkId: number;
  }[];
  pages: number;
  total: number;
}

/** Temu 本地 - 查询尺码数据参数 */
export interface TemuLocalSizeElementsParams {
  catId?: number;
  goodsId?: number;
  pid?: number;
  refPid?: number;
  searchText?: string;
  shopId?: string;
  value?: string;
  vid?: number;
}

/** Temu 本地 - 查询尺码数据响应，VAxios 已自动解包 result。 */
export interface TemuLocalSizeElementsRes {
  sizeSpecElementRule: {
    allowRange: boolean;
    catId: number;
    classId: number;
    className: string;
    isSizeChartRequired: boolean;
    localCodeId: number;
    localCodeName: string;
    needUSSpec: boolean;
    setElementList: {
      allowRange: boolean;
      catId: number;
      classId: number;
      className: string;
      localCodeId: number;
      localCodeName: string;
      needUSSpec: boolean;
      sizeSpecElementList: {
        description: string;
        elementId: number;
        elementName: string;
        necessary: boolean;
        type: number;
        value: string;
      }[];
    }[];
    sizeSpecElementList: {
      description: string;
      elementId: number;
      elementName: string;
      necessary: boolean;
      type: number;
      value: string;
    }[];
    sizeSpecType: number;
  };
}

/** Temu 本地 - 上传视频参数 */
export interface TemuLocalVideoParams {
  shopId?: string;
  url?: string;
}

/** Temu 本地 - 上传视频响应，VAxios 已自动解包 result。 */
export type TemuLocalVideoRes = string;

/**
 * 内容中心接口配置
 */
export const createApi = (defH: VAxios) => {
  const defHttp: any = {
    get: defH.get.bind(defH),
    post: (config: AxiosRequestConfig, options?: RequestOptions) =>
      defH.post.bind(defH)(
        {
          ...config,
          timeout: config.timeout || 60 * 1000 * 5,
        },
        options
      ),
    put: defH.put.bind(defH),
    delete: defH.delete.bind(defH),
  };
  return {
    // 废弃文件
    discard: (
      id: string,
      params: {
        files?: {
          fileIds?: string[];
          jobId?: string;
        }[];
      }
    ) => {
      const fileChunks = chunk(params.files, 20);
      // 将废弃改为串行执行
      return fileChunks.reduce(
        (promise, chunk) =>
          promise.then(() =>
            defHttp.post({
              url: `/aiad/batch/${id}/files/discard`,
              params: {
                files: chunk,
              },
            })
          ),
        Promise.resolve()
      );
    },

    // 恢复废弃文件
    restore: (
      id: string,
      params: {
        files?: {
          fileIds?: string[];
          jobId?: string;
        }[];
      }
    ) => {
      const fileChunks = chunk(params.files, 20);
      // 将恢复改为串行执行
      return fileChunks.reduce(
        (prev, chunk) =>
          prev.then(() =>
            defHttp.post({
              url: `/aiad/batch/${id}/files/restore`,
              params: {
                files: chunk,
              },
            })
          ),
        Promise.resolve()
      );
    },

    getWorkflows: (params: {
      ability?: string;
      batchNo?: string;
      endTime?: string;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
      status?: string;
      userId?: string;
      remark?: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/workflow/creations`,
        params,
      });
    },

    getBatch: (params: {
      ability?: string;
      endTime?: string;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
      status?: string;
      batchNo?: string;
      userId?: string;
      source?: string;
      remark?: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch`,
        params,
        timeout: 60 * 5000,
      });
    },

    getCapacityDetails(params: {
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
    }): Promise<PageWrapper<CapacityDetailInfo>> {
      return defHttp.get({
        url: `/aiad/capacity/details`,
        params,
      });
    },

    getStoreCrawls: (params: {
      endTime?: string;
      page: number;
      pageSize: number;
      probabilities: string;
      shopName: string;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
      userId?: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/store/crawl`,
        params,
      });
    },

    getStoreCrawlDetails: (params: { id: string }) => {
      return defHttp.get({
        url: `/aiad/batch/store/crawl/${params.id}`,
        params,
      });
    },

    addStoreCrawlTortTask: (params: { ids: string[]; tortType: string }) => {
      return defHttp.post({
        url: `/aiad/batch/store/crawl/tort`,
        params,
      });
    },

    deleteStoreCrawl(params: { ids: string[]; recycle: boolean }) {
      return defHttp.delete({
        url: `/aiad/batch/store/crawl`,
        params,
      });
    },

    deleteBatch: (params) => {
      return defHttp.delete({
        url: `/aiad/batch`,
        params,
      });
    },

    getBatchJobs: (
      id,
      params: {
        page: number;
        pageSize: number;
        sortField?: string;
        sortOrder?: string;
        status?: string;
      }
    ) => {
      return defHttp.get({
        url: `/aiad/batch/${id}/jobs`,
        params,
      });
    },

    // 修改结果文件
    updateBatchJobResult: (id, params) => {
      return defHttp.put({
        url: `/aiad/batch/jobs/${id}`,
        params,
      });
    },

    addBatchTextTask: (params: {
      ability: string;
      ar: number;
      arString: string;
      file: string;
      styleCode?: string;
      subStyleCode?: string;
      num?: number;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/txt2img`,
        params,
      });
    },

    addBatchImageTask: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/reimage`,
        params,
      });
    },

    addProductFissionTask: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/product-fission`,
        params,
      });
    },

    addSmartMockupTask: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/smart-mockup`,
        params,
      });
    },

    addSeedPostTask: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/seed_post`,
        params,
      });
    },

    addBatchDetectTask: (params: {
      urls?: string[];
      images: any[];
      extendTag: boolean;
      sourceBatchId?: string;
      patternType?: number;
      multi?: boolean;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/detect`,
        params,
      });
    },

    addBatchTortTask: (params: {
      urls?: string[];
      images: any[];
      extendTag: boolean;
      sourceBatchId?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/tort`,
        params,
      });
    },

    addBatchImage2PrintTask: (params: {
      urls?: string[];
      images: any[];
      extendTag: boolean;
      sourceBatchId?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/image_2_print`,
        params,
      });
    },

    addBatchTKVideoTask: (params: {
      urls?: string[];
      images?: any[];
      imageInfo?: any[];
      videoInfo?: Array<{ videoUrl: string; duration: number }>;
      extendTag?: boolean;
      sourceBatchId?: string;
      mode: 'common' | 'storyboard' | 'viral_remake';
      modelName?: 'v_model' | 's_model';
      resolution?: string;
      prompts?: any[];
      userPrompt?: string;
      modelId?: string;
      modelUrl?: string;
      num?: number;
      language?: string;
      arString?: string;
      duration?: number;
      watermark?: boolean | null;
      remark?: string;
      remarkColor?: string;
      tags?: any[];
      [key: string]: any;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/tk_video`,
        params,
      });
    },

    addBatchTileTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/tile`,
        params,
      });
    },

    addBatchBlendTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/blend`,
        params,
      });
    },

    addBatchMultiSizeTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/multi-size`,
        params,
      });
    },

    addSmartLayoutTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/smart-layout`,
        params,
      });
    },

    addBatchPolyptychTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/polyptych`,
        params,
      });
    },

    addBatchStyleReferenceTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/style-reference`,
        params,
      });
    },

    addBatchMockupTask: (params: {
      images: any[];
      urls?: string[];
      templates: string[];
      random?: boolean;
      extendTag: boolean;
      sourceBatchId?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/mockup`,
        params,
      });
    },

    addBatchDressTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/dress`,
        params,
      });
    },

    addBatchPrintingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/print`,
        params,
      });
    },

    addBatchMemeTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/meme`,
        params,
      });
    },

    addBatchChangeBackgroundTask: (params: {
      backgroundId?: string | number;
      backgroundUrl?: string;
      businessType?: number;
      extendTag?: boolean;
      images?: Array<Record<string, unknown>>;
      leisure?: boolean;
      num?: number;
      prompt?: string;
      referenceType?: 'bg' | 'fg';
      referenceStrength?: number;
      remark?: string;
      remarkColor?: string;
      smartIndex?: boolean;
      source?: string;
      sourceBatchId?: number;
      tags?: number[];
    }) => {
      return defHttp.post({
        url: `/aiad/batch/change-background`,
        params,
      });
    },

    addDIYCompositingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/diy-compositing`,
        params,
      });
    },

    addDIYFulfillmentTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/diy-fulfillment`,
        params,
      });
    },

    addVectorCompositingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/vector-compositing`,
        params,
      });
    },

    //#region 通用模板接口

    // 查询模板
    getCommonTemplates: async (params) => {
      if (params.templateGroupId) {
        const res: any = await defHttp.get({
          url: `/aiad/batch/templates`,
          params: {
            ...params,
            group: params.templateGroupId === 'all' ? undefined : params.templateGroupId,
            type: params.type,
          },
        });
        return {
          ...res,
          items: res.items?.map((item) => {
            return {
              ...item.meta,
              name: item.name,
              templateGroupId: item.group,
              isTemplate: true,
            };
          }),
        };
      }
      return defHttp.get({
        url: `/aiad/common/templates`,
        params,
      });
    },
    // 新增模版
    addCommonTemplate: (params) => {
      return defHttp.post({
        url: `/aiad/common/templates`,
        params,
      });
    },
    // 获取模版信息
    getCommonTemplateInfo: (id) => {
      return defHttp.get({
        url: `/aiad/common/templates/${id}`,
      });
    },
    // 更新模版
    updateCommonTemplate: (id, params) => {
      return defHttp.put({
        url: `/aiad/common/templates/${id}`,
        params,
      });
    },
    // 删除模版
    deleteCommonTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/common/templates/${id}`,
      });
    },

    // 获取模板分组
    getCommonTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/common/templates/groups',
        params,
      });
    },

    // 新增模板分组
    addCommonTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/common/templates/groups',
        params,
      });
    },

    // 修改模板分组
    updateCommonTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/common/templates/groups/${id}`,
        params,
      });
    },

    // 删除模板分组
    deleteCommonTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/common/templates/groups/${id}`,
      });
    },

    // 给模板设置分组
    setCommonTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/common/templates/set-groups `,
        params,
      });
    },

    //#endregion

    addBatchMattingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/matting`,
        params,
      });
    },

    addCutPiecesTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/cuts`,
        params,
      });
    },

    // 裁片图提取任务
    addCutPiecesExtractionTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/cut-drawings`,
        params,
      });
    },

    // 增加裁片尺寸
    addCutDrawingCanvas: (jobId: string | number, params: AddCutDrawingCanvasParams) => {
      return defHttp.post<AddCutDrawingCanvasRes>({
        url: `/aiad/batch/${jobId}/cut-drawing-canvas`,
        params,
      });
    },

    retryTask: (id) => {
      return defHttp.put({
        url: `/aiad/batch/jobs/retry/${id}`,
      });
    },

    retryJobs: (params: { batchId: string | number; jobIds: (string | number)[] }) => {
      return defHttp.post({
        url: `/aiad/batch/jobs/retry`,
        params,
      });
    },

    approveJobs: (params: { batchId: string | number; jobIds: (string | number)[] }) => {
      return defHttp.post({
        url: `/aiad/batch/jobs/approve`,
        params,
      });
    },

    getBatchDetail: (id) => {
      return defHttp.get({
        url: `/aiad/batch/${id}`,
      });
    },

    getBatchDetailList: (params: {
      id: string | number;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
      status?: string | number;
      ids?: string;
      discard?: null | boolean;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/${params.id}/jobs`,
        params,
      });
    },

    getAllFiles: (id) => {
      return defHttp.get<
        {
          id: string;
          debugger: CommonDebugger;
          results: {
            url: string;
            name: string;
            text: string;
          }[];
        }[]
      >({
        url: `/aiad/batch/${id}/files`,
      });
    },

    getMockupTemplateById: (id) => {
      return defHttp.get({
        url: `/aiad/templates/mockup/${id}`,
      });
    },

    getMockupTemplates: (params) => {
      return defHttp.get({
        url: `/aiad/templates/mockup`,
        params,
      });
    },

    deleteMockupTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/templates/mockup/${id}`,
      });
    },

    addMockupTemplate: (params: { design: any; name: string; preview: string }) => {
      return defHttp.post({
        url: `/aiad/templates/mockup`,
        params,
      });
    },

    // 套图模板列表
    getGroupTemplates: async (params) => {
      if (params.templateGroupId) {
        let group = params.templateGroupId;
        let recommend: boolean = null;
        const isPsd = params.templateType === 'psd';
        console.log(params);
        if (group === 'recommend') {
          group = null;
          recommend = !isPsd;
        }

        const res: any = await defHttp.get({
          url: `/aiad/batch/templates`,
          params: {
            ...params,
            group,
            recommend,
            type: isPsd ? 'psd' : 'mockup',
          },
        });
        return {
          ...res,
          items: res.items?.map((item) => {
            return {
              ...item.meta,
              name: item.name,
              templateGroupId: item.group,
              tempPreview: item.preview,
              templateType: isPsd ? 'psd' : 'lingtu',
              isTemplate: true,
            };
          }),
        };
      }
      return defHttp.get({
        url: '/aiad/mock/templates',
        params,
      });
    },
    // 新增套图模板
    addGroupTemplate: (params) => {
      return defHttp.post({
        url: `/aiad/mock/templates`,
        params: {
          templateType: 'lingtu',
          ...params,
        },
      });
    },

    // 修改mockup模板
    updateGroupTemplate: (id, params) => {
      return defHttp.put({ url: `/aiad/mock/templates/${id}`, params });
    },

    // 获取套图模板详情
    getGroupTemplateInfo: (id) => {
      return defHttp.get({
        url: `/aiad/mock/templates/${id}`,
      });
    },
    // 删除套图模板
    deleteGroupTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/mock/templates/${id}`,
      });
    },

    // 获取套图模板分组
    getGroupTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/mock/templates/groups',
        params,
      });
    },

    // 新增套图模板分组
    addGroupTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/mock/templates/groups',
        params,
      });
    },

    // 修改套图模板分组
    updateGroupTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/mock/templates/groups/${id}`,
        params,
      });
    },

    // 删除套图模板分组
    deleteGroupTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/mock/templates/groups/${id}`,
      });
    },

    // 印花模板列表
    getPrintingTemplates: async (params) => {
      if (params.templateGroupId) {
        const res: any = await defHttp.get({
          url: `/aiad/batch/templates`,
          params: {
            ...params,
            group: params.templateGroupId,
            type: 'print',
          },
        });
        return {
          ...res,
          items: res.items?.map((item) => {
            return {
              ...item.meta,
              name: item.name,
              templateGroupId: item.group,
              isTemplate: true,
            };
          }),
        };
      }
      return defHttp.get({
        url: '/aiad/print/templates',
        params,
      });
    },
    // 热门印花模板
    getHotPrintingTemplates: (params) => {
      return defHttp.get({
        url: '/aiad/print/templates/hots',
        params,
      });
    },
    // 新增印花模板
    addPrintingTemplate: (params) => {
      return defHttp.post({
        url: `/aiad/print/templates`,
        params,
      });
    },

    // 修改印花模板
    updatePrintingTemplate: (id, params) => {
      return defHttp.put({ url: `/aiad/print/templates/${id}`, params });
    },

    // 获取印花模板详情
    getPrintingTemplateInfo: (id) => {
      return defHttp.get({
        url: `/aiad/print/templates/${id}`,
      });
    },
    // 删除印花模板
    deletePrintingTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/print/templates/${id}`,
      });
    },
    // 获取印花模板分组
    getPrintingTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/print/templates/groups',
        params,
      });
    },

    // 新增印花模板分组
    addPrintingTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/print/templates/groups',
        params,
      });
    },

    // 修改印花模板分组
    updatePrintingTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/print/templates/groups/${id}`,
        params,
      });
    },

    // 删除印花模板分组
    deletePrintingTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/print/templates/groups/${id}`,
      });
    },

    // 给印花模板设置分组
    setPrintingTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/print/templates/set-groups`,
        params,
      });
    },

    // 查询裁片图模板
    getCutPiecesTemplate: (params) => {
      return defHttp.get({
        url: `/aiad/cutpieces/templates`,
        params,
      });
    },
    // 新增裁片图模版
    addCutPiecesTemplate: (params) => {
      return defHttp.post({
        url: `/aiad/cutpieces/templates`,
        params,
      });
    },
    // 获取生产图模版信息
    getCutPiecesTemplateInfo: (id) => {
      return defHttp.get({
        url: `/aiad/cutpieces/templates/${id}`,
      });
    },
    // 更新生产图模版
    updateCutPiecesTemplate: (id, params) => {
      return defHttp.put({
        url: `/aiad/cutpieces/templates/${id}`,
        params,
      });
    },
    // 删除生产图模版
    deleteCutPiecesTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/cutpieces/templates/${id}`,
      });
    },
    // mockup模板绑定生产图模板
    bindCutPiecesTemplate: (params) => {
      return defHttp.post({
        url: `/aiad/mock/templates/${params.id}/cut`,
        params,
      });
    },

    // 来图定制模板列表
    getDIYTemplates: async (params) => {
      if (params.templateGroupId) {
        const res: any = await defHttp.get({
          url: `/aiad/batch/templates`,
          params: {
            ...params,
            group: params.templateGroupId === 'all' ? undefined : params.templateGroupId,
            type: 'diy',
            name: params.name,
          },
        });
        return {
          ...res,
          items: res.items?.map((item) => {
            return {
              ...item.meta,
              name: item.name,
              templateGroupId: item.group,
              isTemplate: true,
            };
          }),
        };
      }
      return defHttp.get({
        url: '/aiad/diy/templates',
        params,
      });
    },

    // 新增DIY模板
    addDIYTemplate: (params) => {
      return defHttp.post({
        url: `/aiad/diy/templates`,
        params,
      });
    },

    // 修改DIY模板
    updateDIYTemplate: (id, params) => {
      return defHttp.put({ url: `/aiad/diy/templates/${id}`, params });
    },

    // 获取DIY模板详情
    getDIYTemplateInfo: (id) => {
      return defHttp.get({
        url: `/aiad/diy/templates/${id}`,
      });
    },
    // 删除DIY模板
    deleteDIYTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/diy/templates/${id}`,
      });
    },
    // 获取DIY模板分组
    getDIYTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/diy/templates/groups',
        params,
      });
    },

    // 新增DIY模板分组
    addDIYTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/diy/templates/groups',
        params,
      });
    },

    // 修改DIY模板分组
    updateDIYTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/diy/templates/groups/${id}`,
        params,
      });
    },

    // 删除DIY模板分组
    deleteDIYTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/diy/templates/groups/${id}`,
      });
    },

    // 给DIY模板设置分组
    setDIYTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/diy/templates/set-groups`,
        params,
      });
    },

    // 获取Temu模板分组
    getTemuTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/templates/goods/groups',
        params,
      });
    },

    // 新增Temu模板分组
    addTemuTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/templates/goods/groups',
        params,
      });
    },

    // 通过skuid查询关联的DIY模版画布
    getDIYTemplateBySkuId: (skuId: string, params?) => {
      return defHttp.get({
        url: `/aiad/diy/templates/skus/${skuId}`,
        params,
      });
    },

    // 更新skuid关联DIY模版画布
    updateDIYTemplateSkuId: (params) => {
      return defHttp.put({
        url: `/aiad/diy/templates/skus`,
        params,
      });
    },

    // 修改Temu模板分组
    updateTemuTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/templates/goods/groups/${id}`,
        params,
      });
    },

    // 删除Temu模板分组
    deleteTemuTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/templates/goods/groups/${id}`,
      });
    },

    // 给mTemu模板设置分组
    setTemuTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/templates/goods/set-groups`,
        params,
      });
    },

    // 给mockup模板设置分组
    setGroupTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/mock/templates/set-groups`,
        params,
      });
    },

    addWorkflowTask: (params: { images: any[]; sourceBatchId?: string; configId: boolean }) => {
      return defHttp.post({
        url: `/aiad/batch/workflow/creations`,
        params,
      });
    },

    addVectorTask: (params: {
      urls?: string[];
      images: any[];
      sourceBatchId?: string;
      extendTag: boolean;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/vector`,
        params,
      });
    },
    addRemoveTask: (params: { urls: string[] }) => {
      return defHttp.post({
        url: `/aiad/batch/remove`,
        params,
      });
    },
    addColorTask: (params: { urls: string[]; colors: string[] }) => {
      return defHttp.post({
        url: `/aiad/batch/colors`,
        params,
      });
    },
    addTitleTask: (params: {
      prompt?: string;
      urls?: string[];
      images: any[];
      extendTag: boolean;
      sourceBatchId?: string;
      scene?: string;
      category1?: string;
      category2?: string;
      charLimit?: number;
      platform?: string;
      commaSeparated?: boolean;
      blockLibIds?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/title`,
        params,
      });
    },

    addUpscaleTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/upscale`,
        params,
      });
    },

    addUpscaleProduction: (params) => {
      return defHttp.post({
        url: `/aiad/batch/production`,
        params,
      });
    },

    addImageProcessingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/image-processing`,
        params,
      });
    },

    addBatchImageProcessingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/batch-image-processing`,
        params,
      });
    },

    addGroupPhotoTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/group-photo`,
        params,
      });
    },

    addImageEditingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/editor`,
        params,
      });
    },

    addImageExpandTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/expand`,
        params,
      });
    },

    addWatermarkTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/watermark`,
        params,
      });
    },

    addUVLayeringTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/uv-layering`,
        params,
      });
    },

    addEdgeToVectorTask: (params: {
      images: any[];
      edgeToVector: {
        expandMm: number;
        smooth: boolean;
        breakpointCompensate: boolean;
        contourMode: 'outline' | 'withHoles';
      };
      sourceBatchId?: string;
      extendTag?: boolean;
      remark?: string;
      remarkColor?: string;
      tags?: any[];
    }) => {
      return defHttp.post({
        url: `/aiad/batch/edge-to-vector`,
        params,
      });
    },

    addImageTilingTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/tiling`,
        params,
      });
    },

    getCrawls: (params: {
      ability: string;
      endTime?: string;
      page: number;
      pageSize: number;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
      status?: string;
      batchNo?: string;
      userId?: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/crawls`,
        params,
      });
    },

    addRedrawTask: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/redraw`,
        params,
      });
    },

    addVideoTask: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/video`,
        params,
      });
    },

    addPetDressupTask: (params) => {
      return defHttp.post({
        url: `/aiad/batch/pet-dressup`,
        params,
      });
    },

    addPartReplaceTask: (params: {
      extendTag?: boolean;
      num: number;
      images?: {
        afters?: string[];
        before?: string;
        sourceId?: string;
        url?: string;
        sourceJobId?: string;
        prompt?: string;
        afterColors?: string[];
        texture?: { url: string; sourceId: string };
        images?: { url: string; sourceId: string }[];
      }[];
      sourceBatchId?: string;
      source?: string;
      type: string;
      mode: 'lite' | 'pro';
      batch: boolean;
      remark?: string;
      remarkColor?: string;
      tags?: any[];
      smartIndex?: any;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/part-replace`,
        params,
      });
    },

    addCrawlTask: (params: {
      category?: string;
      height?: Record<string, unknown>;
      keyword?: string;
      low?: Record<string, unknown>;
      num?: number;
      platform?: string;
      rate?: boolean;
      sort?: string;
      type?: string;
      urls?: Record<string, unknown>[];
    }) => {
      return defHttp.post({
        url: `/aiad/batch/crawls`,
        params,
      });
    },

    getCrawDetail: (id) => {
      return defHttp.get({
        url: `/aiad/batch/crawls/${id}`,
      });
    },

    getCrawlInfo: () => {
      return defHttp.get<{
        hideNum: Nullable<number>;
        recharge: boolean;
        price: number;
        limit: number;
      }>({
        url: `/aiad/batch/crawls/info`,
      });
    },

    confirmCrawlInfo: (params: { notRemind: boolean }) => {
      return defHttp.post({
        url: `/aiad/batch/crawls/confirm`,
        params,
      });
    },

    getCrawDetailList: (params: {
      id: string | number;
      page: number;
      pageSize: number;
      sortField?: string;
      sortOrder?: string;
      status?: string | number;
      discard?: boolean;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/crawls/${params.id}/jobs`,
        params,
      });
    },

    getCrawPluginList: (params: {
      page: number;
      pageSize: number;
      sortField?: string;
      sortOrder?: string;
      platform?: string;
      status?: string | number;
      startTime?: any;
      endTime?: any;
      userId?: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/crawls/plugin/jobs`,
        params,
      });
    },

    mergeCrawls: (params: {
      fileIds: Array<string | number>;
      ids: Array<string | number>;
      images: string[];
      sourceId: string | number;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/crawls/merge`,
        params,
      });
    },

    //#region 我的空间
    // 查询文件，支持分页
    getFiles: (params: {
      ability: string;
      batchNo: string;
      startTime: string;
      endTime: string;
      page: number;
      pageSize: number;
      sortField: string;
      sortOrder: string;
      tags: number[];
      userId?: string;
      useTimesStart?: number;
      useTimesEnd?: number;
      /** 视频发布状态：unpublished / publishing / scheduled / published */
      videoStatus?: 'unpublished' | 'publishing' | 'scheduled' | 'published';
    }) => {
      return defHttp.post<PageWrapper<IBatchFile>>({
        url: `/aiad/batch/space/files`,
        params,
      });
    },

    // 查询文件侵权风险过滤结果
    getFileTorts: (params: { ids: string[] }) => {
      return defHttp.post({
        url: `/aiad/batch/space/files/tort`,
        params,
      });
    },

    // 文件详情
    getFileDetail: (id) => {
      return defHttp.get<IBatchFile>({
        url: `/aiad/batch/space/files/${id}`,
      });
    },
    // 文件添加标签
    fileAddTags: (params: { ids: string[]; tags: string[]; type?: 0 | 1 }) => {
      return defHttp.post({
        url: `/aiad/batch/space/files/tags`,
        params,
      });
    },
    // 文件删除标签
    fileDeleteTags: (params: { ids; tags: string[] }) => {
      return defHttp.delete({
        url: `/aiad/batch/space/files/tags`,
        params,
      });
    },

    uploadFiles: (
      uploadFiles: { url: string; name?: string; md5?: string; type?: string }[],
      params = {}
    ) => {
      return defHttp.post({
        url: `/aiad/batch/space/files/upload`,
        params: {
          ...params,
          uploadFiles,
        },
      });
    },

    // 批量删除文件
    deleteFiles: (ids, deleted = false) => {
      const idChunks = chunk(ids, 20);
      // 串行执行删除
      return idChunks.reduce(
        (promise, chunk) =>
          promise.then(() =>
            defHttp.delete({
              url: `/aiad/batch/space/files`,
              params: {
                ids: chunk,
                deleted,
              },
            })
          ),
        Promise.resolve()
      );
    },
    //#endregion

    //#region 标签
    // 查询标签组
    getTagGroups: (params?: { ids?; name?; type? }) => {
      return defHttp.get<IBatchTagGroup[]>({
        url: `/aiad/batch/tag/groups`,
        params,
      });
    },
    // 创建标签组
    createTagGroup: (params: { name: string; type?: string }) => {
      return defHttp.post({
        url: `/aiad/batch/tag/groups`,
        params,
      });
    },
    // 更新标签组
    updateTagGroup: (id, params: { name: string; type?: string }) => {
      return defHttp.put({
        url: `/aiad/batch/tag/groups/${id}`,
        params,
      });
    },
    // 删除标签组
    deleteTagGroup: (id, params?: { type?: string }) => {
      return defHttp.delete({
        url: `/aiad/batch/tag/groups/${id}`,
        params,
      });
    },
    // 查询标签
    getTags: (params?: { groupId?: number; ids?: string[]; name?: string; type?: string }) => {
      return defHttp.get<IBatchTag[]>({
        url: `/aiad/batch/tags`,
        params,
      });
    },
    // 创建标签
    createTag: (params?: { groupId?: number; name?: string; type?: string }) => {
      return defHttp.post({
        url: `/aiad/batch/tags`,
        params,
      });
    },
    // 修改标签
    updateTag: (id, params?: { name?: string; type?: string }) => {
      return defHttp.put({
        url: `/aiad/batch/tags/${id}`,
        params,
      });
    },
    // 删除标签
    deleteTag: (id, params?: { type?: string }) => {
      return defHttp.delete({
        url: `/aiad/batch/tags/${id}`,
        params,
      });
    },
    // 标签添加到标签组
    addTagToGroup: (id, groupIds, params?: { type?: string }) => {
      return defHttp.post({
        url: `/aiad/batch/tags/${id}/groups`,
        params: {
          groups: groupIds,
          ...params,
        },
      });
    },
    // 标签从添标签组删除
    removeTagFromGroup: (id, groupIds, params?: { type?: string }) => {
      return defHttp.delete({
        url: `/aiad/batch/tags/${id}/groups`,
        params: {
          groups: groupIds,
          ...params,
        },
      });
    },

    // 判断文件是否有标签
    hasTags: (params: { ids: string[] }) => {
      return defHttp.post({
        url: `/aiad/batch/space/files/has-tags`,
        params,
      });
    },

    //#endregion

    // 预览导出数据
    previewExportData: (params) => {
      return defHttp.post({
        url: '/aiad/batch/export',
        params,
      });
    },
    previewExportDataPages: (params) => {
      return defHttp.post({
        url: '/aiad/batch/export/pages',
        params,
      });
    },

    // 导出数据
    exportData: (params) => {
      return defHttp.post({
        url: '/aiad/batch/exports',
        params,
      });
    },

    // 取消任务
    cancelJob: (id) => {
      return defHttp.delete({
        url: `/aiad/batch/${id}`,
      });
    },

    // #region 文生图提示词相关

    // 获取助手配置
    getModelPrompt: (model: string) => {
      return defHttp.get({
        url: `/aiad/pa/models/${model}`,
      });
    },

    // 执行提示词助手
    executePromptAssist: (params) => {
      return defHttp.post({
        url: `/aiad/pa/models`,
        params,
      });
    },

    // 执行提示词助手
    postPromptAssist: (params) => {
      return defHttp.post({
        url: '/aiad/pa/assist',
        params,
      });
    },

    // 识图转文
    describeImage: async ({ url }) => {
      const data = await defHttp.post({
        url: `/aiad/creations/caption`,
        params: {
          imageUrl: url,
        },
      });
      return data?.promptEn;
    },

    // 提示词模板列表
    getPromptTemplates: (params) => {
      return defHttp.get({
        url: '/aiad/pa/templates',
        params,
      });
    },

    // 保存提示词模板
    savePromptTemplate: (params) => {
      return defHttp.post({
        url: '/aiad/pa/templates',
        params,
      });
    },

    // 更新提示词模板
    updatePromptTemplate: (id, params) => {
      return defHttp.put({
        url: `/aiad/pa/templates/${id}`,
        params,
      });
    },

    // 删除提示词模板
    deletePromptTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/pa/templates/${id}`,
      });
    },

    // 官方预设
    getStandardPromptTemplates: (params) => {
      return defHttp.get({
        url: '/aiad/pa/templates/pre',
        params,
      });
    },

    // 根据提示词裂变
    fissionPrompt: (params) => {
      return defHttp.post({
        url: '/aiad/pa/rewrite',
        params,
      });
    },

    // 解析excel
    parseExcel: (file) => {
      return defHttp.post({
        url: '/aiad/batch/txt2img/excel',
        params: {
          file,
        },
      });
    },

    promptAssistant: (params) => {
      return defHttp.post({
        url: '/aiad/batch/text/assistant',
        params,
      });
    },

    getTemuExcelTemplates: async (params: {
      name?: string;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
      groupId?: string;
      type?: string;
      templateGroupId?: string;
    }) => {
      if (params.templateGroupId) {
        const res: any = await defHttp.get({
          url: `/aiad/batch/templates`,
          params: {
            ...params,
            group: params.templateGroupId === 'all' ? undefined : params.templateGroupId,
            type: 'goods',
          },
        });
        return {
          ...res,
          items: res.items?.map((item) => {
            return {
              ...item.meta,
              createTime: item.createTime,
              modifyTime: item.modifyTime,
              name: item.name,
              templateGroupId: item.group,
              isTemplate: true,
            };
          }),
        };
      }
      return defHttp.get({
        url: '/aiad/templates/goods',
        params,
      });
    },

    getTemuExcelTemplatesById: (id: string) => {
      return defHttp.get({
        url: `/aiad/templates/goods/${id}`,
      });
    },

    parseTemuExcel: (params: {
      file: string;
      name: string;
      type: string;
      includeData?: boolean;
    }) => {
      return defHttp.post({
        url: '/aiad/templates/goods/excel',
        params,
        timeout: 60 * 10000,
      });
    },

    saveTemuExcel: (params: any) => {
      return defHttp.post({
        url: '/aiad/templates/goods',
        params,
      });
    },

    putTemuExcel: (id: any, params: any) => {
      return defHttp.put({
        url: `/aiad/templates/goods/${id}`,
        params,
      });
    },

    deleteTemuExcel: (id: any) => {
      return defHttp.delete({
        url: `/aiad/templates/goods/${id}`,
      });
    },

    getGoodTemplateTypes: () => {
      return defHttp.get({
        url: `/aiad/templates/goods/types`,
      });
    },

    exportTemuTemplate: (params: {
      batchId?: string | number;
      columns: string[];
      ids?: string[];
      templateIds?: string[];
      type?: string;
      dataSource?: string;
      translate?: boolean;
      customFileName?: string;
    }) => {
      return defHttp.post({
        url: '/aiad/batch/export/jobs',
        params,
      });
    },

    getExportTemuJob: (params: {
      ability?: string;
      batchNo?: string;
      endTime?: string;
      jobStatus?: string;
      page: number;
      pageSize: number;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
    }) => {
      return defHttp.get({
        url: '/aiad/batch/export/jobs',
        params,
      });
    },

    getExportTemuJobDetail: (id: string) => {
      return defHttp.get({
        url: `/aiad/batch/export/jobs/${id}`,
      });
    },

    //#endregion

    // 删除采集数据
    deleteCrawlGoods: (id) => {
      return defHttp.delete({
        url: `/aiad/batch/crawl-goods/${id}`,
      });
    },

    // 批量删除采集数据
    deleteCrawlBatchGoods: (params: {
      goods: Array<{
        allDelete: boolean;
        fileIds: string[];
        id: string;
      }>;
      recycle: boolean;
    }) => {
      const goodsChunks = chunk(params.goods, 20);
      return goodsChunks.reduce(
        (promise, chunk) =>
          promise.then(() =>
            defHttp.delete({
              url: `/aiad/batch/crawl-goods/batch`,
              params: {
                recycle: params.recycle,
                goods: chunk,
              },
            })
          ),
        Promise.resolve()
      );
    },

    // 删除采集数据
    deleteCrawlingData: (
      params: Array<{
        allDelete: boolean;
        fileIds: string[];
        id: string;
      }>
    ) => {
      console.log(params);
      return defHttp.delete({
        url: `/aiad/batch/crawl-goods`,
        params,
      });
    },

    // 根据货号搜索
    searchCrawlGoods: (params: { sku?: string; spu?: string }) => {
      return defHttp.get({
        url: `/aiad/batch/export/jobs/sku/images`,
        params,
      });
    },

    // 根据货号搜索
    downloadCrawlGoodUrl: (params: {
      dataId?: string;
      sku?: string;
      spu?: string;
      url?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/batch/export/jobs/sku/download`,
        params,
      });
    },

    // 根据货号批量搜索
    downloadCrawlGoodUrls: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/export/jobs/sku/downloads`,
        params,
      });
    },

    // 增加备注
    remark: (id: string, params: { remark?: string; remarkColor?: string }) => {
      return defHttp.put({
        url: `/aiad/batch/${id}/remark`,
        params,
      });
    },

    getDials: () => {
      return defHttp.get({
        url: `/aiad/batch/dials`,
      });
    },
    get3DDials: () => {
      return defHttp.get({
        url: `/aiad/batch/dials-three`,
      });
    },

    getRedrawStyles: (params: any) => {
      return defHttp.get({
        url: `/aiad/creations/redraw/styles`,
        params,
      });
    },

    syncDetect: (params: { url: string }) => {
      return defHttp.post({
        url: `/aiad/creations/detect?url=${params.url}`,
      });
    },

    getPresets: (params?: { type: string }) => {
      return defHttp.get({
        url: `/aiad/reimage/presets`,
        params,
      });
    },

    updatePresets: (id: string, params: { url: string; meta: Record<string, unknown> }) => {
      return defHttp.put({
        url: `/aiad/reimage/presets/${id}`,
        params,
      });
    },

    postPresets: (params: { type: string; url: string; meta?: Object }) => {
      return defHttp.post({
        url: `/aiad/reimage/presets`,
        params,
      });
    },
    deletePresets: (id: string) => {
      return defHttp.delete({
        url: `/aiad/reimage/presets/${id}`,
      });
    },

    addWorkflowTemplate: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/workflows`,
        params,
      });
    },

    getWorkflowTemplates: async (params) => {
      if (params.templateGroupId) {
        const res: any = await defHttp.get({
          url: `/aiad/batch/templates`,
          params: {
            ...params,
            group: params.templateGroupId,
            type: 'workflow',
          },
        });
        return {
          ...res,
          items: res.items?.map((item) => {
            return {
              ...item.meta,
              name: item.name,
              templateGroupId: item.group,
              isTemplate: true,
            };
          }),
        };
      }
      return defHttp.get({
        url: '/aiad/batch/workflows',
        params,
      });
    },
    getWorkflowIpoddyTemplates: async (params) => {
      const res: any = await defHttp.get({
        url: `/aiad/batch/templates`,
        params: {
          ...params,
          group: params.templateGroupId,
          type: 'workflow',
        },
      });
      return {
        ...res,
        items: res.items?.map((item) => {
          return {
            ...item.meta,
            name: item.name,
            templateGroupId: item.group,
            isTemplate: true,
          };
        }),
      };
    },

    getWorkflowDetail: (id: string) => {
      return defHttp.get({
        url: `/aiad/batch/workflow/creations/${id}`,
      });
    },

    // 完成审核
    approveWorkflow: (params: { batchId: string; workflowId: string }) => {
      return defHttp.post({
        url: '/aiad/batch/workflow/creations/approve',
        params,
      });
    },

    // 修改工作流模板
    updateWorkflowTemplate: (id, params) => {
      return defHttp.put({ url: `/aiad/batch/workflows/${id}`, params });
    },

    // 修改工作流模板名称
    updateWorkflowTemplateName: (id, params: { name: string }) => {
      return defHttp.post({ url: `/aiad/batch/workflows/${id}/name`, params });
    },

    // 获取工作流模板详情
    getWorkflowTemplateInfo: (id) => {
      return defHttp.get({
        url: `/aiad/batch/workflows/${id}`,
      });
    },
    // 删除工作流模板
    deleteWorkflowTemplate: (id) => {
      return defHttp.delete({
        url: `/aiad/batch/workflows/${id}`,
      });
    },

    // 获取工作流模板分组
    getWorkflowTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/batch/workflows/groups',
        params,
      });
    },

    // 新增工作流模板分组
    addWorkflowTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/batch/workflows/groups',
        params,
      });
    },

    // 修改工作流模板分组
    updateWorkflowTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/batch/workflows/groups/${id}`,
        params,
      });
    },

    // 删除工作流模板分组
    deleteWorkflowTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/batch/workflows/groups/${id}`,
      });
    },

    // 给工作流模板设置分组
    setWorkflowTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/batch/workflows/set-groups`,
        params,
      });
    },

    // 导入模板
    importTemplateByIds: (ids) => {
      return defHttp.post({
        url: `/aiad/mock/templates/import`,
        params: {
          ids,
        },
      });
    },

    // 创建单个任务
    createJob: (params) => {
      return defHttp.post({
        url: '/aiad/batch/jobs',
        params,
      });
    },

    // 查询任务明细
    getJobDetail: (id, batchId) => {
      return defHttp.get({
        url: `/aiad/batch/${batchId}/jobs/${id}`,
      });
    },

    // 识图转文
    imageToCaption: (params) => {
      return defHttp.post({
        url: '/aiad/creations/caption',
        params,
      });
    },

    // 抹除孔洞
    removeHole: (url) => {
      return defHttp.post({
        url: `/aiad/creations/remove-hole?url=${url}`,
      });
    },

    // 通过md5查询文件是否存在
    getFileByMd5: (md5: string, name?: string) => {
      return defHttp.post({
        url: `/aiad/batch/space/files/md5`,
        params: {
          md5,
          name,
        },
      });
    },

    createFileFromSku: (params: {
      uploadFiles: {
        name?: string;
        title?: string;
        url?: string;
      }[];
    }) => {
      return defHttp.post({
        url: `/aiad/batch/space/files/sku`,
        params,
      });
    },
    // 保存为官方模板
    saveAsOfficialTemplate: (type, id) => {
      return defHttp.post({
        url: `/aiad/batch/templates/${type}/${id}`,
      });
    },
    // batch店铺鉴权回调
    authCallback: (type, params?: any) => {
      return defHttp.post({
        url: `/aiad/authorize/${type}`,
        params,
      });
    },

    authLinkCallback: (type, params?: any) => {
      return defHttp.post({
        url: `/aiad/authorize/${type}/link`,
        params,
      });
    },

    // 查询店铺
    getShops: (params: any) => {
      return defHttp.get({
        url: `/aiad/shops`,
        params,
      });
    },

    /** 查询店铺列表（支持 types 多平台） */
    queryShops(params: QueryShopsParams): Promise<QueryShopsRes> {
      return defHttp.post({
        url: `/aiad/shops/query`,
        params,
      });
    },

    /** 查询店铺详情 */
    getShop(id: string): Promise<GetShopRes> {
      return defHttp.get({
        url: `/aiad/shops/${id}`,
      });
    },

    // 修改店铺
    updateShop: (id: any, params: any) => {
      return defHttp.put({
        url: `/aiad/shops/${id}`,
        params,
      });
    },

    // 解绑店铺
    unBindShop: (id: any) => {
      return defHttp.delete({
        url: `/aiad/shops/${id}/authorize`,
      });
    },

    // 获取shein授权链接
    getSheInAuthUrl: (params: { name: string; type: string; groupId?: string }) => {
      return defHttp.post({
        url: `/aiad/authorize/shein/link`,
        params,
      });
    },

    // 修改店铺状态
    updateShopStatus: (id: any, status: any) => {
      return defHttp.put({
        url: `/aiad/shops/${id}/status/${status}`,
      });
    },

    // 获取平台
    getShopPlatforms: () => {
      return defHttp.get({
        url: `/aiad/shops/platforms`,
      });
    },

    // 查询上架模版列表
    getShopTemplates: (params: any) => {
      return defHttp.get({
        url: `/aiad/shop/templates`,
        params,
      });
    },

    // 查询上架模版列表（POST，支持 templateIds / excludeTypes 数组）
    queryShopTemplates: (params: {
      page?: number;
      pageSize?: number;
      shopId?: string;
      name?: string;
      type?: string;
      groupId?: string;
      excludeTypes?: string[];
      templateIds?: string[];
      [key: string]: any;
    }) => {
      return defHttp.post({
        url: `/aiad/shop/templates/query`,
        params,
      });
    },

    // 获取商品模版
    getProductTemplate: (params: any) => {
      return defHttp.get({
        url: `/aiad/shop/templates/product`,
        params,
        timeout: 1000 * 60 * 5,
      });
    },

    /** Temu 空白商品模板骨架（createType=1） */
    getBlankShopTemplate: (params: { shopId: string | number }) => {
      return defHttp.get({
        url: `/aiad/shop/templates/blank`,
        params,
      });
    },

    // 获取Temu里的商品模版
    getTemuProducts: (params: any) => {
      return defHttp.get({
        url: `/aiad/temu/products`,
        params,
      });
    },

    // 获取商品模版
    getShopTemplatesById: (id: any) => {
      return defHttp.get({
        url: `/aiad/shop/templates/${id}`,
      });
    },

    saveShopTemplate: (id: any, params: any) => {
      return defHttp.put({
        url: `/aiad/shop/templates/${id}`,
        params,
      });
    },

    /** 创建上架模版（POST /aiad/shop/templates） */
    addShopTemplate: (params: any) => {
      return defHttp.post({
        url: `/aiad/shop/templates`,
        params,
      });
    },

    removeShopTemplate: (id: any) => {
      return defHttp.delete({
        url: `/aiad/shop/templates/${id}`,
      });
    },

    updateShopTemplateName: (
      id: any,
      params: {
        name: string;
      }
    ) => {
      return defHttp.put({
        url: `/aiad/shop/templates/${id}/name`,
        params,
      });
    },

    // 获取刊登模板分组
    getShopTemplateGroups: (params) => {
      return defHttp.get({
        url: '/aiad/shop/templates/groups',
        params,
      });
    },

    // 新增刊登模板分组
    addShopTemplateGroup: (params) => {
      return defHttp.post({
        url: '/aiad/shop/templates/groups',
        params,
      });
    },

    // 修改刊登模板分组
    updateShopTemplateGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/shop/templates/groups/${id}`,
        params,
      });
    },

    // 删除刊登模板分组
    deleteShopTemplateGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/shop/templates/groups/${id}`,
      });
    },

    // 给刊登模板设置分组
    setShopTemplateGroup: (params) => {
      return defHttp.put({
        url: `/aiad/shop/templates/set-groups`,
        params,
      });
    },

    // 获取店铺分组
    getShopGroups: (params) => {
      return defHttp.get({
        url: '/aiad/shops/groups',
        params,
      });
    },

    // 新增店铺分组
    addShopGroup: (params) => {
      return defHttp.post({
        url: '/aiad/shops/groups',
        params,
      });
    },

    // 修改店铺分组
    updateShopGroup: (id, params) => {
      return defHttp.put({
        url: `/aiad/shops/groups/${id}`,
        params,
      });
    },

    // 删除店铺分组
    deleteShopGroup: (id) => {
      return defHttp.delete({
        url: `/aiad/shops/groups/${id}`,
      });
    },

    // 给店铺设置分组
    setShopGroup: (params) => {
      return defHttp.put({
        url: `/aiad/shops/set-groups`,
        params,
      });
    },

    // 获取商品列表
    getGoods: (params: any) => {
      return defHttp.post({
        url: `/aiad/goods`,
        params,
      });
    },

    // 获取商品详情
    getGoodsDetail: (id: any) => {
      return defHttp.get({
        url: `/aiad/goods/${id}`,
      });
    },

    /** 查询商品发布信息 */
    queryPublishGoods: (id: string): Promise<QueryPublishGoodsRes> => {
      return defHttp.get({
        url: `/aiad/goods/${id}/publish`,
      });
    },

    // 获取商品详情
    getStoreCrawlDetail: (id: any) => {
      return defHttp.get({
        url: `/aiad/batch/store/crawl/${id}`,
      });
    },

    // 删除商品
    removeGoods: (params: any) => {
      return defHttp.delete({
        url: `/aiad/goods`,
        params,
      });
    },

    // 采集数据移动
    crawlingToGoods: (params: any) => {
      return defHttp.post({
        url: `/aiad/goods/crawling`,
        params,
      });
    },

    mockupToGoods: (params: any) => {
      return defHttp.post({
        url: `/aiad/goods/mockup`,
        params,
      });
    },

    creationToGoods: (params: {
      batchId: string | number;
      jobs: Array<{ jobId: string | number; files?: number[] }>;
      tags: string[];
      saveType?: 0 | 1; // 0.按图片，1.按任务
    }) => {
      return defHttp.post({
        url: `/aiad/goods/creation`,
        params,
      });
    },

    getGoodsTasks: (params: any) => {
      return defHttp.post({
        url: `/aiad/publish/jobs/query`,
        params,
      });
    },

    // 商品添加标签
    goodsAddTags: (params: { ids: string[]; tags: string[]; type?: 0 | 1 }) => {
      return defHttp.post({
        url: `/aiad/goods/tags`,
        params,
      });
    },

    // 商品删除标签
    goodsRemoveTags: (params: { ids: string[]; tags: string[]; type?: 0 | 1 }) => {
      return defHttp.delete({
        url: `/aiad/goods/tags`,
        params,
      });
    },

    // 添加商品图片
    goodsAddImages: (
      id: string,
      params: {
        contents: {
          contentId?: string;
          id?: Record<string, unknown>;
          url?: string;
        }[];
        type: 'print' | 'production';
      }
    ) => {
      return defHttp.post({
        url: `/aiad/goods/${id}/images`,
        params,
      });
    },

    // 商品删除轮播图
    goodsRemoveImage: (id: string, imageId: string) => {
      return defHttp.delete({
        url: `/aiad/goods/${id}/images/${imageId}`,
      });
    },

    // 删除商品图片
    goodsDeleteImage: (
      id: string, // 商品ID
      params: {
        contentId?: string; // 内容ID
        type:
          | 'print'
          | 'production'
          | 'cut_pieces'
          | 'compress'
          | 'image'
          | 'batch_watermark'
          | 'change_background'
          | 'part_replace';
      }
    ) => {
      return defHttp.delete({
        url: `/aiad/goods/${id}/images`,
        params,
      });
    },

    // 删除店铺采集图片
    storeDeleteImage: (params: {
      contentIds?: string[]; // 内容ID列表
      id?: string;
    }) => {
      return defHttp.delete({
        url: `/aiad/batch/store/crawl/contents`,
        params,
      });
    },

    goodsPublishJobs: (params: any) => {
      return defHttp.post({
        url: `/aiad/publish/jobs`,
        params,
      });
    },

    getGoodsBatchDetailList: (params: any) => {
      return defHttp.get({
        url: `/aiad/publish/jobs/${params.id}/details`,
        params,
      });
    },

    getGoodsBatchDetail: (id) => {
      return defHttp.get({
        url: `/aiad/publish/jobs/${id}`,
      });
    },

    // 取消上架任务
    cancelShelfJob: (id) => {
      return defHttp.delete({
        url: `/aiad/publish/jobs/${id}`,
      });
    },

    // 删除刊登任务
    deletePublishJob: (
      id: string,
      params: {
        deleteGoods: boolean;
      }
    ) => {
      return defHttp.post({
        url: `/aiad/publish/jobs/delete/${id}`,
        params,
      });
    },

    /** 插件刊登-认领明细 */
    claim(id: number): Promise<ClaimResult> {
      return defHttp.post({
        url: `/aiad/publish/plugin/details/${id}/claim`,
      });
    },

    /** 插件刊登-回传明细结果 */
    reportPublishDetail(id: number | string, params: ReportParams): Promise<boolean> {
      return defHttp.post({
        url: `/aiad/publish/plugin/details/${id}/report`,
        params,
      });
    },

    // 更新模板分组权限设置
    updateTemplateGroupPermission: (params: any) => {
      return defHttp.put({
        url: `/aiad/templates/group-perms/details`,
        params,
      });
    },
    // 获取模板分组权限设置
    getTemplateGroupPermission: (params: any) => {
      return defHttp.get({
        url: `/aiad/templates/group-perms/details`,
        params,
      });
    },

    // 查询我的常用工具列表
    getMyTools: () => {
      return defHttp.get({
        url: `/aiad/batch/my-tools`,
      });
    },

    // 添加我的常用工具列表
    addMyTools: (params: any) => {
      return defHttp.put({
        url: `/aiad/batch/my-tools`,
        params,
      });
    },

    // 删除我的常用工具列表
    removeMyTools: (params: any) => {
      return defHttp.delete({
        url: `/aiad/batch/my-tools`,
        params,
      });
    },

    // 查询AI试衣模特
    getTryOnModel: (params: any) => {
      return defHttp.get({
        url: `/aiad/batch/tryon-model`,
        params,
      });
    },

    // 查询AI试衣背景
    getTryOnBgs: (params: any) => {
      return defHttp.get({
        url: `/aiad/batch/tryon-bgs`,
        params,
      });
    },

    // 查询AI试衣姿势
    getTryOnPose: () => {
      return defHttp.get({
        url: `/aiad/batch/tryon-pose-v2`,
      });
    },

    // 查询AI试衣模特V2
    getTryOnModelV2: (params: {
      age?: string;
      gender?: string;
      page: number;
      pageSize: number;
      skin?: string;
      sortField: string;
      sortOrder: string;
      status: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/tryon-model-v2`,
        params,
      });
    },

    // 查询AI试衣背景V2
    getTryOnBgsV2: (params: {
      category1: string;
      page: number;
      pageSize: number;
      scene: string;
      sortField: string;
      sortOrder: string;
      status: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/tryon-background-v2`,
        params,
      });
    },

    // 查询AI试衣形态
    getTryOnStance: (params: {
      page: number;
      pageSize: number;
      sortField: string;
      sortOrder: string;
      status: string;
      type: string;
    }) => {
      return defHttp.get({
        url: `/aiad/batch/tryon-stance-v2`,
        params,
      });
    },

    createShop: (params: any) => {
      return defHttp.post({
        url: `/aiad/shops`,
        params,
      });
    },

    sizeSpecList: (params: { catId: string; shopId: string }) => {
      return defHttp.get({
        url: `/aiad/temu/size-templates`,
        params,
      });
    },

    temuModels: (params: { catId: string; shopId: string }) => {
      return defHttp.get({
        url: `/aiad/temu/models`,
        params,
      });
    },

    temuAccessories: (params: any) => {
      return defHttp.post({
        url: `/aiad/temu/accessories`,
        params,
      });
    },

    temuFreightTemplates: (params: { siteIds: string[]; shopId: string }) => {
      return defHttp.post({
        url: `/aiad/temu/freightTemplates`,
        params,
      });
    },

    temuWarehouses: (params: { siteIds: string[]; shopId: string }) => {
      return defHttp.post({
        url: `/aiad/temu/warehouses`,
        params,
      });
    },

    updateTemuVideo: (params: { shopId: string; url: string }) => {
      return defHttp.post({
        url: `/aiad/temu/video`,
        params,
        timeout: 60 * 100000,
      });
    },

    pollingTemuVideo: (params: { shopId: string; vid: string }) => {
      return defHttp.get({
        url: `/aiad/temu/video`,
        params,
      });
    },

    // 查询分类属性（Temu）
    temuAttributes(params: TemuAttributesParams): Promise<TemuAttributesRes> {
      return defHttp.get({
        url: `/aiad/temu/attributes`,
        params,
      });
    },

    /** Temu 类目列表；parentCatId 空=根级；空白模板浏览用 */
    temuCategories(params: {
      shopId: string | number;
      parentCatId?: number | string;
    }): Promise<TemuCategoryItem[]> {
      return defHttp.get({
        url: `/aiad/temu/categories`,
        params,
      });
    },

    /** Temu 搜索类目；返回聚合路径列表，空白模板搜索下拉用 */
    temuSearchCategories(params: {
      keyword: string;
      shopId: string | number;
    }): Promise<TemuSearchCategoryPath[]> {
      return defHttp.get({
        url: `/aiad/temu/search-category`,
        params,
      });
    },

    /** Temu 尺码组（空白模板）；非服饰/无尺码组类目可能返回 null */
    temuSizechartClass(params: {
      shopId: string | number;
      catId?: number | string;
      classId?: number | string;
    }): Promise<TemuSizechartClassRes | null> {
      return defHttp.get({
        url: `/aiad/temu/sizechart-class`,
        params,
      });
    },

    /** Temu 尺码表元数据（空白模板） */
    temuSizechartMeta(params: {
      shopId: string | number;
      catId?: number | string;
      classId?: number | string;
    }): Promise<TemuSizechartMetaRes> {
      return defHttp.get({
        url: `/aiad/temu/sizechart-meta`,
        params,
      });
    },

    /** Temu - 查询父规格列表 */
    queryParentSpecList(params: QueryParentSpecListParams): Promise<QueryParentSpecListRes> {
      return defHttp.get({
        url: `/aiad/temu/parent-specs`,
        params,
      });
    },

    /** Temu - 创建自定义规格（返回规格 ID） */
    createGoodsSpec(params: CreateGoodsSpecParams): Promise<CreateGoodsSpecRes> {
      return defHttp.post({
        url: `/aiad/temu/goods-spec`,
        params,
      });
    },

    // 查询分类属性（Temu 本地）
    temuLocalAttributes(params: TemuLocalAttributesParams): Promise<TemuLocalAttributesRes> {
      return defHttp.get({
        url: `/aiad/temu-local/attributes`,
        params,
      });
    },

    // 查询合规补充信息（Temu 本地）
    temuLocalComplianceExtraTemplates(
      params: TemuLocalComplianceExtraTemplatesParams
    ): Promise<TemuLocalComplianceExtraTemplatesRes> {
      return defHttp.post({
        url: `/aiad/temu-local/compliance-templates`,
        params,
      });
    },

    // 查询运费模板（Temu 本地）
    temuLocalFreightTemplates(
      params: TemuLocalFreightTemplatesParams
    ): Promise<TemuLocalFreightTemplatesRes> {
      return defHttp.get({
        url: `/aiad/temu-local/freight-templates`,
        params,
      });
    },

    // 查询商品列表（Temu 本地）
    getTemuLocalProducts(params: TemuLocalProductsParams): Promise<TemuLocalProductsRes> {
      return defHttp.get({
        url: `/aiad/temu-local/products`,
        params,
      });
    },

    // 查询尺码数据（Temu 本地）
    temuLocalSizeElements(params: TemuLocalSizeElementsParams): Promise<TemuLocalSizeElementsRes> {
      return defHttp.get({
        url: `/aiad/temu-local/size-templates`,
        params,
      });
    },

    // 上传视频（Temu 本地）
    uploadTemuLocalVideo(params: TemuLocalVideoParams): Promise<TemuLocalVideoRes> {
      return defHttp.post({
        url: `/aiad/temu-local/video`,
        params,
        timeout: 60 * 100000,
      });
    },

    getSheInSites(params: { shopId: string }) {
      return defHttp.get({
        url: `/aiad/shein/sites`,
        params,
      });
    },

    getSheInWarehouses(params: { shopId: string }) {
      return defHttp.get({
        url: `/aiad/shein/warehouses`,
        params,
      });
    },

    /** Shein - 创建视频转换任务 */
    createVideoTransformTask(
      params: CreateVideoTransformTaskParams
    ): Promise<CreateVideoTransformTaskRes> {
      return defHttp.post({
        url: `/aiad/shein/video-transform-task`,
        params,
      });
    },

    /** Shein - 查询视频转换任务结果 */
    getVideoTransformTasks(
      params: GetVideoTransformTasksParams
    ): Promise<GetVideoTransformTasksRes> {
      return defHttp.post({
        url: `/aiad/shein/video-transform-tasks/query`,
        params,
      });
    },

    /** TikTok - 查询跨境店铺仓库列表 */
    getSellerWarehouses(params: { shopId: string }): Promise<GetSellerWarehousesRes> {
      return defHttp.get({
        url: `/aiad/tiktok/warehouses`,
        params,
      });
    },

    // 抠图修边
    mattingTrimming: (params) => {
      return defHttp.post<{
        url: string;
      }>({ url: `/aiad/creations/matting-Trimming`, params });
    },

    // 获取erp授权地址
    getErpAuthorize(type: string, params?: any) {
      return defHttp.get({
        url: `/aiad/erp/authorize/${type}`,
        params,
      });
    },

    unBindErp(type: string) {
      return defHttp.delete({
        url: `/aiad/erp/authorize/${type}`,
      });
    },

    // 宠物换装官方精选着装
    getPetOfficialPetStyle(params?: any) {
      return defHttp.get<
        Array<{
          id: number;
          preview: string;
          previewExt: string;
          image: string;
          prompt: string;
        }>
      >({
        url: `/aiad/batch/pet-outfit`,
        params,
      });
    },

    // 添加自定义宠物换装
    addPetStyle(image?: string) {
      return defHttp.post({
        url: `/aiad/batch/pet-outfit-self`,
        params: {
          image,
        },
      });
    },

    // 删除自定义宠物换装
    deletePetStyle(id: number) {
      return defHttp.delete({
        url: `/aiad/batch/pet-outfit-self/${id}`,
      });
    },
    // 查询自定义宠物换装
    queryPetStyle() {
      return defHttp.get<
        Array<{
          id: number;
          image: string;
        }>
      >({
        url: `/aiad/batch/pet-outfit-self`,
      });
    },

    // 获取案例列表
    getTextCases(params) {
      return defHttp.get({
        url: `/aiad/batch/case-center/cases`,
        params,
      });
    },

    // 获取案例分类
    getTextCaseCategories() {
      return defHttp.get({
        url: '/dict/ops/cases-types',
      });
    },

    // 设为案例
    setTextCase(params: {
      ability?: string;
      image: string;
      width: number;
      height: number;
      prompt?: string;
      meta?: any;
      firstCategory?: string;
      type: string;
      status: string;
      tags?: string[];
    }) {
      return defHttp.post({
        url: '/aiad/batch/case-center/cases',
        params,
      });
    },

    getCaseType: () => {
      return defHttp.get({
        url: '/aiad/batch/case-center/category',
      });
    },

    getDefaultCaseType: () => {
      return defHttp.get({
        url: '/aiad/batch/case-center/category/default',
      });
    },

    getDefaultCaseTags: () => {
      return defHttp.get({
        url: '/aiad/batch/case-center/tags',
      });
    },

    // 获取直播预告列表
    getLivePreviewList() {
      return defHttp.get({
        url: '/aiad/batch/announce',
      });
    },

    // 查询空间大小
    getSpaceSize(params) {
      return defHttp.get({
        url: '/aiad/batch/space/size',
        params,
      });
    },

    // 查询成员空间大小
    getSpaceSizeMembers(params?: {
      recycled?: boolean;
      spaceType?: 'crawl' | 'material' | 'template' | 'smart_index';
      userId?: number;
    }): Promise<
      {
        num: number;
        size: number;
        userId: string;
      }[]
    > {
      return defHttp.get({
        url: '/aiad/batch/space/size/members',
        params,
      });
    },

    // 修改文件名称
    setFileName(id, name) {
      return defHttp.post({
        url: `/aiad/batch/space/${id}/name`,
        params: {
          name,
        },
      });
    },

    // 清空回收站
    clearupRecycle(params: { includeDisabled: boolean }) {
      return defHttp.delete({
        url: '/aiad/batch/space/recycle/clear',
        params,
      });
    },

    // 恢复内容
    restoreFiles(ids) {
      const idChunks = chunk(ids, 20);
      // 将恢复改为串行执行
      return idChunks.reduce(
        (promise, chunk) =>
          promise.then(() =>
            defHttp.post({
              url: '/aiad/batch/space/recycle/restore',
              params: { ids: chunk },
            })
          ),
        Promise.resolve()
      );
    },

    // 恢复内容
    getClearJob(id: string) {
      return defHttp.get({
        url: `/aiad/batch/space/clear/${id}`,
      });
    },

    // 创建清除job
    createClearJob(params: {
      abilities: string[];
      beforeDays: number;
      excludeTags?: number[];
      spaceType?: string[];
      tags?: number[];
      userId?: string;
    }) {
      return defHttp.post({
        url: `/aiad/batch/space/clear`,
        params,
      });
    },

    // 保存清除规则
    createClearRule(params: {
      abilities: string[];
      beforeDays: string;
      excludeTags?: number[];
      spaceType?: string[];
      tags?: number[];
      userId?: string;
    }) {
      return defHttp.post({
        url: `/aiad/batch/clear/rules`,
        params,
      });
    },

    getClearRule(params: {
      endTime?: string;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
      status?: string;
    }) {
      return defHttp.get({
        url: `/aiad/batch/clear/rules`,
        params,
      });
    },

    // 文件清理统计
    getClearStat(params: {
      abilities: string[];
      beforeDays: string;
      excludeTags?: number[];
      spaceType?: string[];
      tags?: number[];
      userId?: string;
    }) {
      return defHttp.post({
        url: `/aiad/batch/space/clear/stat`,
        params,
      });
    },

    // 删除清理规则
    deleteClearRule(id: string) {
      return defHttp.delete({
        url: `/aiad/batch/clear/rules/${id}`,
      });
    },

    // 更改清理规则
    updateClearRuleStatus(id: string, status: string) {
      return defHttp.put({
        url: `/aiad/batch/clear/rules/${id}/${status}`,
      });
    },

    // 更改清理规则状态
    updateClearRule(
      id: string,
      params: {
        abilities: string[];
        beforeDays: string;
        excludeTags?: number[];
        spaceType?: string[];
        tags?: number[];
        userId?: string;
      }
    ) {
      return defHttp.put({
        url: `/aiad/batch/clear/rules/${id}`,
        params,
      });
    },

    batchCreateShopOperator(params: { shopIds: string[]; userIds: string[] }) {
      return defHttp.put({
        url: '/aiad/shops/operators/batch',
        params,
      });
    },

    saveShopOperator(id: string, params: { shopId: string; userIds: string[] }) {
      return defHttp.put({
        url: `/aiad/shops/${id}/operators`,
        params,
      });
    },

    updateTitle(params: {
      goodsId: number | string;
      id?: number | string;
      language?: string;
      value: string;
    }) {
      return defHttp.put({
        url: `/aiad/goods/titles`,
        params,
      });
    },

    searchQuestion(params: { question: string }) {
      return defHttp.post({
        url: `/la/knowledge-fragments/search`,
        params,
      });
    },

    podCaseEvent: (params) => {
      return defHttp.post({
        url: `/ops/pod/cases`,
        params,
      });
    },

    feedback: (params) => {
      return defHttp.post({
        url: `/aiad/batch/feedbacks`,
        params,
      });
    },

    report: (params) => {
      return defHttp.post<void>({
        url: '/aiad/podcase/report',
        params,
      });
    },

    // 添加我的POD自定义
    addMyPodCustom: (params: any) => {
      return defHttp.post({
        url: `/aiad/batch/self/${params.type}`,
        params,
      });
    },
    // 查询我的POD自定义
    getMyPodCustom: (type) => {
      return defHttp.get({
        url: `/aiad/batch/self/${type}`,
      });
    },
    // 删除我的POD自定义
    deleteMyPodCustom: (id: number) => {
      return defHttp.delete({
        url: `/aiad/batch/self/${id}`,
      });
    },

    // 修改我的POD自定义
    updateMyPodCustom: (params) => {
      return defHttp.put({
        url: `/aiad/batch/self/${params.type}/${params.id}`,
        params,
      });
    },

    // 查询POD搜索历史记录
    getPodSearchRecord: () => {
      return defHttp.get({
        url: `/aiad/pod/records`,
      });
    },

    // 删除POD搜索历史记录
    removePodSearchRecord: (params: { keyword: string }) => {
      return defHttp.delete({
        url: `/aiad/pod/records?keyword=${params.keyword}`,
      });
    },

    // 上报POD搜索历史记录
    createPodSearchRecord: (params: { detail?: string; keyword?: string; type?: string }) => {
      return defHttp.post({
        url: `/aiad/pod/records`,
        params,
      });
    },

    // 查询推送记录列表
    getPushRecords: (params: {
      page: number;
      pageSize: number;
      searchType?: string;
      keyword?: string;
      platformShopIds?: string[];
      status?: string;
      startTime?: string;
      endTime?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/publish/push-records/query`,
        params,
      });
    },

    getOrderRecords(params: {
      page?: number;
      pageSize?: number;
      status?: string;
      orderNo?: string; // 订单号
      goodsId?: string; // 商品ID
      productId?: string; // 商品货品ID
      shopId?: number; // 店铺ID
      shopName?: string; // 店铺名称
      sortField?: string; // 排序字段
      sortOrder?: 'ascend' | 'descend'; // 排序顺序
      sourceCode?: string; // 来源编码
      startTime?: string;
      endTime?: string;
      pushStartTime?: string;
      pushEndTime?: string;
    }) {
      return defHttp.get({
        url: `/aiad/batch/orders`,
        params,
      });
    },

    // 获取 Access Token 列表
    getGoodsAccessToken(): Promise<
      {
        id: number;
        userId: number;
        token: string;
        createTime: string;
        platform: string;
        scopes: string[];
      }[]
    > {
      return defHttp.get({
        url: `/aiad/batch/goods/access-token`,
      });
    },

    // 创建 Access Token
    postGoodsAccessToken(params: { platform: string; scopes: string[] }): Promise<{
      id: number;
      userId: number;
      token: string;
    }> {
      return defHttp.post({
        url: `/aiad/batch/goods/access-token`,
        params,
      });
    },

    // 更新 Access Token
    putGoodsAccessToken(id: number, params: { platform: string; scopes: string[] }) {
      return defHttp.put({
        url: `/aiad/batch/goods/access-token/${id}`,
        params,
      });
    },

    // 删除 Access Token
    deleteGoodsAccessToken(id: number) {
      return defHttp.delete({
        url: `/aiad/batch/goods/access-token/${id}`,
      });
    },

    // 获取货盘授权配置
    getGoodsAccessTokenConfig(): Promise<{
      apps: {
        id: number;
        code: string;
        name: string;
        scopes: {
          code: string;
          name: string;
        }[];
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/batch/goods/access-token/config`,
      });
    },
    // 获取当前版本信息
    getCurrentVersion: () => {
      return defHttp.get({
        url: `/aiad/batch/version`,
      });
    },
    // 获取当前活动信息
    getCurrentActivity: () => {
      return defHttp.get({
        url: `/aiad/pod/marketing-activity`,
      });
    },

    createSmartIndex: (params: {
      ids: string[];
      repeatCheck?: boolean;
      repeatDelete?: boolean;
      tort?: boolean;
      type?: 'match' | 'search';
    }) => {
      return defHttp.post({
        url: `/aiad/batch/smart-index`,
        params,
      });
    },

    deleteSmartIndex: (params: { ids: string[]; deleted?: boolean }) => {
      return defHttp.delete({
        url: `/aiad/batch/smart-index`,
        params,
      });
    },

    getSmartIndexJobs: (params: any) => {
      return defHttp.get({
        url: `/aiad/batch/smart-index/jobs`,
        params,
      });
    },

    getBatchSmartIndexDetailList: (params: any) => {
      return defHttp.get({
        url: `/aiad/batch/smart-index/jobs/${params.id}/details`,
        params,
      });
    },

    getSmartIndexJob: (id: string) => {
      return defHttp.get({
        url: `/aiad/batch/smart-index/jobs/${id}`,
      });
    },

    cancelSmartIndexJob: (id: string | number) => {
      return defHttp.delete({
        url: `/aiad/batch/smart-index/${id}`,
      });
    },

    uploadSmartIndex: (params: {
      ids: string[];
      repeatCheck?: boolean;
      tort?: boolean;
      type?: 'match' | 'search';
    }) => {
      return defHttp.post({
        url: `/aiad/batch/smart-index/upload`,
        params,
      });
    },

    getBatchSmartIndexenable: () => {
      return defHttp.put({
        url: `/aiad/batch/smart-index`,
      });
    },

    mattingToTiff: (params: {
      cmyk?: boolean;
      format?: string;
      resolution?: number;
      resolutionUnit?: string;
      url?: string;
    }) => {
      return defHttp.post({
        url: `/aiad/creations/covert`,
        params,
      });
    },

    mattingToPsd: (params: { mask?: string; preview?: string; url?: string }) => {
      return defHttp.post({
        url: `/aiad/creations/mattingToPsd`,
        params,
      });
    },

    // 导入订单
    postBatchOrdersImport(params: {
      imageType: string;
      orderNo: string;
      platform: string;
      shopName: string;
      sku: string;
      sourceCode: string;
      orderImages?: { id: string; url: string }[];
    }): Promise<void> {
      return defHttp.post(
        {
          url: `/aiad/batch/orders/import`,
          params,
        },
        {
          errorMessageMode: 'null',
        }
      );
    },

    // 导出订单
    postBatchOrdersExport(params: { ids: string[] }): Promise<string> {
      return defHttp.post({
        url: `/aiad/batch/orders/export`,
        params,
      });
    },

    postBatchOrdersContents(params: {
      contents: {
        contentId?: string;
        id?: Record<string, unknown>;
        url?: string;
      }[];
      orderId: string;
      type: 'image' | 'print' | 'production';
    }): Promise<void> {
      return defHttp.post({
        url: `/aiad/batch/orders/contents`,
        params,
      });
    },

    deleteBatchOrdersContents(params: { contents: string[]; orderId: string }): Promise<void> {
      return defHttp.delete({
        url: `/aiad/batch/orders/contents`,
        params,
      });
    },

    getOmniPatternExtractions(): Promise<any> {
      return defHttp.get({
        url: `/aiad/batch/omni-pattern-extractions`,
      });
    },

    getImageRecreateConfigList(): Promise<any> {
      return defHttp.get({
        url: `/aiad/pod/image-recreate-config/list`,
      });
    },

    getProductImageFissionConfigList(): Promise<
      {
        code: string;
        enable: boolean;
        exampleImage: string;
        id: number;
        name: string;
        prompt: string;
        sort: number;
      }[]
    > {
      return defHttp.get({
        url: `/aiad/pod/product-image-fission-config/list`,
      });
    },

    getFissionObjectConfigList(): Promise<
      {
        code: string;
        enable: boolean;
        id: number;
        name: string;
        sort: number;
      }[]
    > {
      return defHttp.get({
        url: `/aiad/pod/fission-object-config/list`,
      });
    },

    saveEvent(params: any): Promise<any> {
      return defHttp.post({
        url: `/ops/pod/events`,
        params,
      });
    },

    getCasesBanners() {
      return defHttp.get({
        url: `/aiad/pod/banners`,
      });
    },

    getPodToolExamplesPage(params: {
      ability: string;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
    }): Promise<
      PageWrapper<{
        ability: string;
        content: string;
        exampleImages: string[];
        id: number;
        isNew: boolean;
        materialImages: string[];
        title: string;
      }>
    > {
      return defHttp.get({
        url: `/aiad/pod/tool-examples/page`,
        params,
      });
    },

    repeatCheck(params: { abilities?: string[]; id?: string; url?: string }) {
      return defHttp.post({
        url: `/aiad/batch/space/repeat`,
        params,
      });
    },

    // uv生成psd
    uvPsdExport(params) {
      return defHttp.post({
        url: `/uv/psd-export`,
        params,
      });
    },

    // 查询全托管品牌列表
    getFullServiceBrands(params: { shopId: string }): Promise<{
      brands: {
        approvalStatus: string;
        id: string;
        name: string;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/brands`,
        params,
      });
    },

    // 查询全托管类目列表
    getFullServiceCategories(params: {
      pageOffset: number;
      pageSize: number;
      shopId: string;
    }): Promise<{
      categories: {
        id: string;
        isLeaf: boolean;
        nameCn: string;
        nameEn: string;
        parentId: string;
        status: string;
        version: string;
      }[];
      pageOffset: number;
      pageSize: number;
      totalCount: number;
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/categories/cache`,
        params,
      });
    },

    // 查询全托管类目属性
    getFullServiceCategoryAttributes(params: { categoryId: string; shopId: string }): Promise<{
      attributes: {
        id: string;
        isMutipleSelection: boolean;
        isRequired: boolean;
        nameEn: string;
        nameZh: string;
        requiredRegions: Record<string, unknown>[];
        requirementConditions: {
          attributeId: string;
          attributeValueIds: Record<string, unknown>[];
        }[];
        type: string;
        valueType: string;
        values: {
          id: string;
          nameEn: string;
          nameZh: string;
        }[];
      }[];
      regionAttributes: {
        attributes: {
          id: string;
          isMutipleSelection: boolean;
          isRequired: boolean;
          nameEn: string;
          nameZh: string;
          requiredRegions: Record<string, unknown>[];
          requirementConditions: {
            attributeId: string;
            attributeValueIds: Record<string, unknown>[];
          }[];
          type: string;
          valueType: string;
          values: {
            id: string;
            nameEn: string;
            nameZh: string;
          }[];
        }[];
        region: string;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/category-attributes`,
        params,
      });
    },

    // 查询全托管类目资质
    getFullServiceCategoryCertifications(params: { categoryId: string; shopId: string }): Promise<{
      certifications: {
        description: string;
        isRequired: boolean;
        qualifications: {
          description: string;
          exampleUrl: string;
          fileTypes: Record<string, unknown>[];
          maxFileCount: number;
          type: string;
        }[];
        regionCode: string;
        requirementConditions: {
          attributeId: string;
          attributeValueIds: Record<string, unknown>[];
        }[];
        type: string;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/category-certifications`,
        params,
      });
    },

    // 查询全托管类目详情
    getFullServiceCategoryInfo(params: { categoryId: string; shopId: string }): Promise<{
      firstKeyAttributeId: string;
      id: string;
      isAllowedJit: boolean;
      isCustomKeyAttribute: boolean;
      isEnabled: boolean;
      isGarment: boolean;
      isLeaf: boolean;
      level: number;
      nameEn: string;
      nameZh: string;
      parentId: string;
      secondKeyAttributeId: string;
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/category-info`,
        params,
      });
    },

    // 查询全托管类目图片上传要求
    getFullServiceImageUploadRequirements(params: { categoryId: string; shopId: string }): Promise<{
      imageUploadRequirements: {
        configDetails: {
          isRequired: boolean;
          lengthMax: number;
          lengthMin: number;
          lengthRate: number;
          materialShowType: string;
          materialUseType: {
            code: string;
            isRequired: boolean;
            quantityCeiling: number;
            quantityFloor: number;
          }[];
          maxSize: number;
          quantityCeiling: number;
          quantityFloor: number;
          widthMax: number;
          widthMin: number;
          widthRate: number;
        }[];
        picSetType: Record<string, unknown>[];
      }[];
      picUploadWayConfigs: Record<string, unknown>[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/image-upload-requirements`,
        params,
      });
    },

    // 查询全托管类目尺码组
    getFullServiceSizeGroups(params: { categoryId: string; shopId: string }): Promise<{
      sizeGradingData: {
        sizeGradingRequired: boolean;
        sizeGroups: {
          id: string;
          name: string;
          sizeSegements: {
            sizeCode: string;
            sizeId: string;
          }[];
        }[];
      };
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/size-groups`,
        params,
      });
    },

    // 查询全托管尺码表模版
    getFullServiceGradingTemplates(params: { shopId: string }): Promise<{
      gradingTemplates: {
        id: string;
        name: string;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/grading-templates`,
        params,
      });
    },

    // 查询全托管供应商制造商列表
    getFullServiceSupplierManufacturers(params: { categoryId: string; shopId: string }): Promise<{
      manufacturerList: {
        address: string;
        email: string;
        id: string;
        manufacturerName: string;
        phoneNumber: {
          phoneNumber: string;
          phoneNumberRegion: string;
        };
        registeredTradeName: string;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/supplier-manufacturers`,
        params,
      });
    },

    // 查询全托管欧盟责任人列表
    getFullServiceSupplierRps(params: { shopId: string }): Promise<{
      rpInfos: {
        address: {
          city: string;
          country: string;
          province: string;
          street: string;
          street2: string;
        };
        companyName: string;
        createTime: number;
        defaultRP: boolean;
        email: string;
        id: string;
        name: {
          firstName: string;
          lastName: string;
          middleName: string;
        };
        phoneNumber: {
          phoneNumber: string;
          phoneNumberRegion: string;
        };
        rpName: string;
        type: number;
        updateTime: number;
        version: number;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/supplier-rps`,
        params,
      });
    },

    // 查询本地托管销售仓库列表
    getFullServiceSellerSalesWarehouse(params: { shopId: string }): Promise<{
      warehouses: {
        warehouseId: string;
        warehouseName: string;
      }[];
    }> {
      return defHttp.get({
        url: `/aiad/tiktok-full/seller-sales-warehouse`,
        params,
      });
    },

    /** 创作者账号列表（分页） */
    accounts(params: AccountsParams): Promise<AccountsRes> {
      return defHttp.get({
        url: `/aiad/creator/accounts`,
        params,
      });
    },

    /** 查询单个创作者 */
    account(id: number): Promise<AccountRes> {
      return defHttp.get({
        url: `/aiad/creator/accounts/${id}`,
      });
    },

    /** 禁用创作者 */
    disable(id: number): Promise<DisableRes> {
      return defHttp.post({
        url: `/aiad/creator/accounts/${id}/disable`,
      });
    },

    /** 启用创作者 */
    enable(id: number): Promise<EnableRes> {
      return defHttp.post({
        url: `/aiad/creator/accounts/${id}/enable`,
      });
    },

    /** 解绑创作者 */
    unbind(id: number): Promise<UnbindRes> {
      return defHttp.post({
        url: `/aiad/creator/accounts/${id}/unbind`,
      });
    },

    /** 获取创作者授权链接 */
    authorizeUrl(params: AuthorizeUrlParams): Promise<AuthorizeUrlRes> {
      return defHttp.post({
        url: `/aiad/creator/authorize-url`,
        params,
      });
    },

    /** 创作者授权回调 */
    callback(params: CallbackParams): Promise<CallbackRes> {
      return defHttp.post({
        url: `/aiad/creator/callback`,
        params,
      });
    },

    /** 发布记录列表（分页） */
    publishList(params: PublishListParams): Promise<PublishListRes> {
      return defHttp.post({
        url: `/aiad/creator/publish/query`,
        params,
      });
    },

    /** 提交发布 */
    publish(params: PublishParams): Promise<PublishRes> {
      return defHttp.post({
        url: `/aiad/creator/publish`,
        params,
      });
    },

    /** 带货背景音乐搜索 */
    music(params: MusicParams): Promise<MusicRes> {
      return defHttp.get({
        url: `/aiad/creator/publish/music`,
        params,
      });
    },

    /** 查询预检当日剩余发布数量 */
    precheckNum(creatorId: number): Promise<PrecheckNumRes> {
      return defHttp.get({
        url: `/aiad/creator/publish/precheck/num`,
        params: { creatorId },
      });
    },

    /** 商品列表 */
    products(params: ProductsParams): Promise<ProductsRes> {
      return defHttp.get({
        url: `/aiad/creator/publish/products`,
        params,
      });
    },

    /** 查询发布详情 */
    publishDetail(id: number): Promise<PublishDetailRes> {
      return defHttp.get({
        url: `/aiad/creator/publish/${id}`,
      });
    },

    /** 取消发布 */
    cancelPublish(id: number): Promise<CancelPublishRes> {
      return defHttp.post({
        url: `/aiad/creator/publish/${id}/cancel`,
      });
    },

    /** 删除发布记录 */
    deletePublish(id: number): Promise<DeletePublishRes> {
      return defHttp.delete({
        url: `/aiad/creator/publish/${id}`,
      });
    },

    // 批量推送印花-生产图
    postBatchPush(params: { orderIds: (string | number)[] }): Promise<(string | number)[]> {
      return defHttp.post({
        url: `/aiad/batch/push`,
        params,
      });
    },

    // 推送记录列表
    getBatchPushBatches(params: {
      batchNo?: string;
      page?: number;
      pageSize?: number;
      pushEndTime?: string;
      pushStartTime?: string;
      sortField?: string;
      sortOrder?: string;
      status?: string;
      userId?: number | string;
    }): Promise<{
      items: {
        batchNo: string;
        createBy: string;
        createTime: string;
        failCount: number;
        id: number;
        modifyBy: string;
        modifyTime: string;
        sourceCode: string;
        status: string;
        successCount: number;
        totalCount: number;
        userName: string;
      }[];
      pages: number;
      total: number;
    }> {
      return defHttp.get({
        url: `/aiad/batch/push/batches`,
        params,
      });
    },

    // 终止推送
    postBatchPushBatchesTerminate(id: number | string): Promise<boolean> {
      return defHttp.post({
        url: `/aiad/batch/push/batches/${id}/terminate`,
      });
    },

    // 推送详情
    getBatchPushBatchesById(id: number | string): Promise<{
      batchNo: string;
      createBy: string;
      createTime: string;
      failCount: number;
      id: number;
      items: {
        createBy: string;
        createTime: string;
        failReason: string;
        goodsId: number;
        goodsImages: {
          batchId: number;
          contentId: number;
          id: number;
          jobId: string;
          secondType: string;
          url: string;
        }[];
        id: number;
        imageType: string;
        imageUrls: string[];
        modifyBy: string;
        modifyTime: string;
        orderId: string;
        orderNo: string;
        platform: string;
        productId: string;
        requestId: string;
        shopId: number;
        shopName: string;
        status: string;
        type: string;
      }[];
      modifyBy: string;
      modifyTime: string;
      sourceCode: string;
      status: string;
      successCount: number;
      totalCount: number;
      userName: string;
    }> {
      return defHttp.get({
        url: `/aiad/batch/push/batches/${id}`,
      });
    },

    // 查询发布内容状态
    getAiadCreatorPublishContents(params: { contentIds?: string[] }): Promise<{
      items?: {
        contentId: string;
        createBy: string;
        createName: string;
        creatorId: string;
        creatorName: string;
        productCoverUrl: string;
        productId: string;
        productPrice: string;
        productTitle: string;
        publishTime: string;
        /** 是否定时发布：0 否 / 1 是 */
        isSchedule?: 0 | 1;
        /** 视频发布状态：unpublished / publishing / scheduled / published */
        status: 'unpublished' | 'publishing' | 'scheduled' | 'published';
        videoTitle: string;
      }[];
      contents?: {
        contentId: string;
        createBy: string;
        createName: string;
        creatorId: string;
        creatorName: string;
        productCoverUrl: string;
        productId: string;
        productPrice: string;
        productTitle: string;
        publishTime: string;
        /** 是否定时发布：0 否 / 1 是 */
        isSchedule?: 0 | 1;
        /** 视频发布状态：unpublished / publishing / scheduled / published */
        status: 'unpublished' | 'publishing' | 'scheduled' | 'published';
        videoTitle: string;
      }[];
    }> {
      return defHttp.post({
        url: `/aiad/creator/publish/contents`,
        params,
      });
    },

    /** 视频拼接任务 */
    addBatchVideoComposeTask(params: {
      extendTag?: boolean;
      leisure?: boolean;
      num?: number;
      remark?: string;
      remarkColor?: string;
      smartIndex?: boolean;
      source?: string;
      sourceBatchId?: number;
      tags?: (string | number)[];
      transition?: string;
      videos?: Array<
        Array<{
          name?: string;
          sourceId?: number | string;
          url?: string;
          sourceType?: string;
          sourceJobId?: number | string;
          [key: string]: any;
        }>
      >;
    }): Promise<{
      failed: number;
      id: number;
      succeed: number;
    }> {
      return defHttp.post({
        url: `/aiad/batch/video_compose`,
        params,
      });
    },

    /** 第三方视频平台链接下载 */
    postAiadPodThirdPlatformDownload(params: { url: string }): Promise<{
      title: string;
      url: string;
    }> {
      return defHttp.post({
        url: `/aiad/pod/third-platform-download`,
        params,
      });
    },

    /** 查询清理任务 */
    postAiadBatchClearJobs(params?: {
      endTime?: string;
      page?: number;
      pageSize?: number;
      sortField?: string;
      sortOrder?: string;
      startTime?: string;
      status?: string[];
      source?: string;
      userId?: string;
    }): Promise<{
      items: {
        createBy: string;
        createTime: string;
        createUserName: string;
        excludeTagNames: string[];
        finished: number;
        id: number;
        modifyBy: string;
        modifyTime: string;
        num: number;
        progress: number;
        rule: {
          abilities: string[];
          admin: boolean;
          beforeDays: number;
          excludeTags: number[];
          jobId: number;
          orgId: string;
          recycled: boolean;
          spaceType: string[];
          tags: number[];
          userId: number;
        };
        source: string;
        status: string;
        tagNames: string[];
        userName: string;
      }[];
      pages: number;
      total: number;
    }> {
      return defHttp.post({
        url: '/aiad/batch/clear/jobs',
        params,
      });
    },

    getBatchFulfillmentAutomations(params: {
      page?: number;
      pageSize?: number;
      sku?: string;
      templateName?: string;
    }): Promise<{
      items: {
        canvases: {
          autoCompose: boolean;
          autoPush: boolean;
          canvasId: string;
          canvasName: string;
          canvasSize: string;
          contentTags: string;
          id: number;
          skus: string;
        }[];
        fillRule: string;
        id: number;
        params: Record<string, unknown>;
        status: string;
        templateId: string;
        templateName: string;
        templatePreview: string;
      }[];
      pages: number;
      total: number;
    }> {
      return defHttp.get({
        url: '/aiad/batch/fulfillment-automations',
        params,
      });
    },

    postBatchFulfillmentAutomations(params: {
      canvases: {
        autoCompose?: boolean;
        autoPush?: boolean;
        canvasId?: string;
        canvasName?: string;
        canvasSize?: string;
        contentTags?: string;
        id?: number;
        skus?: string;
      }[];
      fillRule?: string;
      params?: Record<string, unknown>;
      remark?: string;
      remarkColor?: string;
      tags?: number[];
      templateId: string;
      templateName?: string;
      templatePreview?: string;
    }): Promise<{
      canvases: {
        autoCompose: boolean;
        autoPush: boolean;
        canvasId: string;
        canvasName: string;
        canvasSize: string;
        contentTags: string;
        id: number;
        skus: string;
      }[];
      fillRule: string;
      id: number;
      params: Record<string, unknown>;
      status: string;
      templateId: string;
      templateName: string;
      templatePreview: string;
    }> {
      return defHttp.post({
        url: '/aiad/batch/fulfillment-automations',
        params,
      });
    },

    putBatchFulfillmentAutomations(
      id: number,
      params: {
        canvases: {
          autoCompose?: boolean;
          autoPush?: boolean;
          canvasId?: string;
          canvasName?: string;
          canvasSize?: string;
          contentTags?: string;
          id?: number;
          skus?: string;
        }[];
        fillRule?: string;
        id?: number;
        isAdmin?: boolean;
        params?: Record<string, unknown>;
        templateId: string;
        templateName?: string;
        templatePreview?: string;
      }
    ): Promise<void> {
      return defHttp.put({
        url: `/aiad/batch/fulfillment-automations/${id}`,
        params,
      });
    },

    putBatchFulfillmentAutomationsCanvasesSwitch(
      canvasRowId: number,
      params: {
        autoCompose?: boolean;
        autoPush?: boolean;
        canvasRowId?: number;
        isAdmin?: boolean;
      }
    ): Promise<void> {
      return defHttp.put({
        url: `/aiad/batch/fulfillment-automations/canvases/${canvasRowId}/switch`,
        params,
      });
    },

    deleteBatchFulfillmentAutomations(id: number): Promise<void> {
      return defHttp.delete({
        url: `/aiad/batch/fulfillment-automations/${id}`,
      });
    },

    // 查询 DIY 模版画布关联的 SKU
    getDiyTemplateSkus(templateId: number | string): Promise<
      Array<{
        canvasId: string;
        diyTemplateId: string;
        skuId: string;
        type: string;
      }>
    > {
      return defHttp.get({
        url: `/aiad/diy/templates/${templateId}/skus`,
      });
    },

    // 修改任务备注
    putAiadBatchJobsRemark(params: {
      batchId?: number | string;
      jobIds?: (number | string)[];
      remark?: string;
    }): Promise<void> {
      return defHttp.put({
        url: `/aiad/batch/jobs/remark`,
        params,
      });
    },

    // 修改批量任务结果
    updateBatchJobsResult(params: {
      batchId?: number | string;
      jobIds?: (number | string)[];
      data?: any;
    }): Promise<void> {
      return defHttp.put({
        url: `/aiad/batch/cut-jobs/${params.jobIds}`,
        params: {
          batchId: params.batchId,
          ...params.data,
        },
      });
    },

    // 根据原图和矢量生成 pdf
    getBatchFulfillmentGeneratePdf(params: {
      imageUrl: string;
      svgUrl: string;
    }): Promise<{ url: string }> {
      return defHttp.post({
        url: `/aiad/creations/pathsToPdf`,
        params,
      });
    },

    /** 图文发布记录列表（分页） */
    postAiadCreatorPhotoPublishQuery(params: PhotoPublishListParams): Promise<PhotoPublishListRes> {
      return defHttp.post({
        url: `/aiad/creator/photo-publish/query`,
        params,
      });
    },

    /** 提交图文发布 */
    postAiadCreatorPhotoPublish(
      params: CreatorPhotoPublishRequest
    ): Promise<CreatorPhotoPublishRes> {
      return defHttp.post({
        url: `/aiad/creator/photo-publish`,
        params,
      });
    },

    /** 店铺类目-合集查询 */
    getAiadCreatorPhotoPublishCategoryCollections(
      params: CategoryCollectionParams
    ): Promise<CategoryCollectionRes> {
      return defHttp.get({
        url: `/aiad/creator/photo-publish/category-collections`,
        params,
      });
    },

    /** 删除图文发布记录 */
    deleteAiadCreatorPhotoPublish(id: number): Promise<boolean> {
      return defHttp.delete({
        url: `/aiad/creator/photo-publish/${id}`,
      });
    },

    /** 取消图文发布 */
    postAiadCreatorPhotoPublishCancel(id: number): Promise<boolean> {
      return defHttp.post({
        url: `/aiad/creator/photo-publish/${id}/cancel`,
      });
    },

    /** 查图文发布详情 */
    getAiadCreatorPhotoPublish(id: number): Promise<PhotoPublishInfoDTO> {
      return defHttp.get({
        url: `/aiad/creator/photo-publish/${id}`,
      });
    },
  };
};
